#!/usr/bin/env node

// Harden the stdio transport BEFORE any other module import — once a native
// binding (better-sqlite3, parcel/watcher) or a deprecation warning writes a
// stray byte to stdout the JSON-RPC framing is corrupted for the entire
// session. We force UTF-8 and route stdout writes to stderr until the MCP
// server is wired (see src/server/transport-hardening.ts).
import { hardenStdio } from './server/transport-hardening.js';
import { installProcessSafetyNet } from './server/process-safety-net.js';
import { armBoundedExit, DAEMON_SHUTDOWN_DEADLINE_MS } from './server/bounded-shutdown.js';
import {
  clearOwnDaemonPidFile,
  checkDaemonRuntimeIntact,
  describeStopContext,
  ephemeralServeHttpRefusal,
  getLaunchdLastExit,
  logPreviousExit,
  PID_REASSERT_INTERVAL_MS,
  reassertOwnDaemonPidFile,
  startDaemonLogRotation,
  writeOwnDaemonPidFile,
} from './daemon/lifecycle.js';
import { installDaemonExitBreadcrumb, noteDaemonShutdownReason } from './daemon/exit-breadcrumb.js';
import {
  logUncleanStopDiagnosis,
  readRecentOomCrashes,
  readRecentStallAlerts,
  STALL_ALERTS_FILENAME,
  summarizeStallAlerts,
} from './daemon/unclean-stop.js';
import { startHeapGuard } from './daemon/heap-guard.js';
import {
  printTelemetryNoticeOnce,
  readDaemonReliabilityCounters,
  recordDaemonCleanStop,
  recordDaemonStart,
} from './telemetry/usage-ping.js';

if (process.argv.includes('serve') || process.argv.length === 2) {
  hardenStdio();
}

import { randomUUID } from 'node:crypto';
import fs from 'node:fs';
import path from 'node:path';
import { fileURLToPath } from 'node:url';
import { Command } from 'commander';

declare const PKG_VERSION_INJECTED: string;
const PKG_VERSION =
  typeof PKG_VERSION_INJECTED !== 'undefined' ? PKG_VERSION_INJECTED : '0.0.0-dev';

import http from 'node:http';
import type { Socket } from 'node:net';
import { StreamableHTTPServerTransport } from '@modelcontextprotocol/sdk/server/streamableHttp.js';
import { stripRedundantSchemaKeyword } from './server/schema-shim.js';
import { isInitializeRequest } from '@modelcontextprotocol/sdk/types.js';
import { aiTracker } from './ai/index.js';
import { detectCoverageRecursive } from './analytics/tech-detector.js';
import { addCommand } from './cli/add.js';
import { analyticsCommand, benchmarkCommand } from './cli/analytics.js';
import { askCommand } from './cli/ask.js';
import { blastCommand } from './cli/blast.js';
import { bundlesCommand } from './cli/bundles.js';
import { checkCommand } from './cli/check.js';
import { clientsCommand } from './cli/clients.js';
import { ciReportCommand } from './cli/ci.js';
import { scanSecurityCommand } from './cli/scan-security.js';
import { daemonCommand } from './cli/daemon.js';
import { consentCommand } from './cli/consent.js';
import { detectLlmCommand } from './cli/detect-llm.js';
import { doctorCommand } from './cli/doctor.js';
import { savingsCommand } from './cli/savings.js';
import { evalCommand } from './cli/eval.js';
import { exportSecurityContextCommand } from './cli/export-security-context.js';
import { initCommand } from './cli/init.js';
import { installAppCommand } from './cli/install-app.js';
import { memoryCommand } from './cli/memory.js';
import {
  pruneCommand,
  pruneIndexDir,
  sweepOrphanStatusSentinels,
  sweepTopLevelOrphanDbs,
} from './cli/prune.js';
import { removeCommand } from './cli/remove.js';
import { searchCommand } from './cli/search.js';
import { statusCommand } from './cli/status.js';
import { subprojectCommand } from './cli/subproject.js';
import { upgradeCommand } from './cli/upgrade.js';
import { sweepImplicitProjects, sweepMissingRoots } from './registry.js';
import { visualizeCommand } from './cli/visualize.js';
import type { TraceMcpConfig } from './config.js';
import { loadConfig, loadGlobalConfigRaw, validateConfigUpdate } from './config.js';
import { saveGlobalSettingsJsonc } from './config-jsonc.js';
import { isDaemonRunning } from './daemon/client.js';
import {
  describeIndexFileOutcome,
  dispatchIndexFile,
  indexFilePathForProject,
  resolveIndexFileProjectRoot,
} from './cli/index-file.js';
import { buildHealthPayload, withMissingRoots } from './daemon/health-payload.js';
import { buildApiProjectsList } from './daemon/api-projects-payload.js';
import { DaemonIdleMonitor } from './daemon/idle-monitor.js';
import { MemoryScheduler } from './memory/scheduler/memory-scheduler.js';
import { ProjectManager } from './daemon/project-manager.js';
import type { ManagedProject } from './daemon/project-manager.js';
import { createDaemonProjectRelay } from './daemon/project-relay.js';
import {
  acceptReindexFile,
  beginReindex,
  countReindexingProjects,
  handleReindexFile,
  isProjectStopping,
  isReindexing,
  markProjectStopping,
} from './daemon/reindex-file-handler.js';
import { startVitalsLog } from './daemon/vitals-log.js';
import { armStartupWatchdog } from './daemon/startup-watchdog.js';
import { getDroppedEventStats } from './indexer/watcher.js';
import { runStdioSession, StdioSession } from './daemon/router/session.js';
// Statically imported (unlike session.ts's own default dynamic import) so
// `serve`'s SnapshotBackend fast path (TRA-948) resolves it near-instantly
// from this already-loaded reference below, instead of paying a first-load
// cost inside its <400ms budget — cli.js already carries this whole
// dependency tree via its other commands, so there is nothing to save by
// deferring it here the way the thin proxy entry does (TRA-970).
import { SnapshotBackend } from './daemon/router/snapshot-backend.js';
import { initializeDatabase } from './db/schema.js';
import { Store } from './db/store.js';
import {
  DAEMON_LOG_PATH,
  DECISIONS_DB_PATH,
  DEFAULT_DAEMON_PORT,
  ensureGlobalDirs,
  EPHEMERAL_INDEX_DIR,
  getAutoRegisterMode,
  GLOBAL_CONFIG_PATH,
  INDEX_DIR,
  TOPOLOGY_DB_PATH,
} from './global.js';
import { DecisionStore } from './memory/decision-store.js';
import { sweepOrphanedSessionDbs } from './daemon/router/session-db.js';
import { IndexingPipeline, readIndexTruncation } from './indexer/pipeline.js';
import {
  type GuardEnforceTier,
  installGuardHook,
  installLifecycleHooks,
  uninstallGuardHook,
  uninstallLifecycleHooks,
} from './init/hooks.js';
import { attachFileLogging, logger } from './logger.js';
import { pruneProjectConfigSections } from './config-jsonc.js';
import { TRACE_MCP_HOME } from './global.js';
import { ensureInitialized, warmUpGrammars } from './parser/tree-sitter.js';
import { checkBindHost, isLoopbackHost } from './daemon/bind-host.js';
import { PluginRegistry } from './plugin-api/registry.js';
import {
  detectGitWorktree,
  findProjectRoot,
  hasRootMarkers,
  resolveServeRoots,
} from './project-root.js';
import { isDangerousProjectRoot, setupProject } from './project-setup.js';
import { sweepEphemeralTopology } from './daemon/project-artifacts.js';
import {
  getProject,
  listProjects,
  pruneStaleProjects,
  resolveDbPath,
  resolveRegisteredAncestor,
  sweepEphemeralDbs,
  updateLastIndexed,
} from './registry.js';
import { isKnownSubproject, resolveDeepestKnownRoot } from './subproject/resolve.js';
import { getWorktreeDelta, resolveWorktreeLink, summarizeWorktreeDelta } from './worktree-delta.js';
import { resolveWorktreeIndexSettings, WorktreeIndexManager } from './daemon/worktree-index.js';
import {
  buildProjectNotFoundError,
  buildResolutionFailureError,
  extractRpcId,
} from './daemon/mcp-error-response.js';
import { resolveProjectForMcpRequest } from './daemon/mcp-project-router.js';
import { recordUnresolvableResolution } from './daemon/resolution-telemetry.js';
import {
  collectIdleSessions,
  dropSessionBookkeeping,
  teardownProjectBookkeeping as teardownProjectBookkeepingImpl,
} from './daemon/project-bookkeeping.js';
import {
  clearKeyForTerminalEvent as clearProgressKeyForTerminalEvent,
  maybePruneOnHighWatermark as maybePruneProgressThrottle,
  shouldEmitThrottledEvent as shouldEmitProgressEvent,
} from './daemon/progress-throttle.js';
import { handleAskSessionsRequest } from './api/ask-sessions-routes.js';
import { handleDashboardRequest } from './api/dashboard-routes.js';
import { handleJournalStatsRequest, type JournalStatsContext } from './api/journal-stats-routes.js';
import { handleMemoryRequest } from './api/memory-routes.js';
import { handleProjectStatsRequest } from './api/project-stats-routes.js';
import { buildProjectFilesQuery } from './api/project-files-query.js';
import { parseProjectSecurityQuery } from './api/project-security-query.js';
import { buildSymbolsSearchQuery } from './api/symbols-search-query.js';
import { buildMemoryReport } from './daemon/memory-report.js';
import { getTreeCacheStats } from './parser/tree-cache.js';
import { buildJournalEvent, buildJournalSnapshot } from './server/journal-broadcast.js';
import { createServer } from './server/server.js';
import { SubprojectManager } from './subproject/manager.js';
import { buildGraphData, generateHtml } from './tools/analysis/visualize.js';
import { scanCodeSmells } from './tools/quality/code-smells.js';
import { scanSecurity } from './tools/quality/security-scan.js';
import { TopologyStore } from './topology/topology-db.js';
import { checkAndInstallUpdate, scheduleBackgroundUpdate } from './updater.js';
import { atomicWriteJson, sweepOrphanTmpFilesUnderHome } from './utils/atomic-write.js';
import { sweepOrphanDbSidecars } from './utils/db-family.js';
import { EventLoopLagMonitor } from './utils/event-loop.js';
import { StallWatchdog } from './utils/stall-watchdog.js';
import { sweepSessionFiles } from './session/sweeper.js';
import { sqliteUtcToIso } from './utils/sqlite-time.js';

/**
 * Auto-discover subprojects: after indexing, detect services within the project
 * and register each as a subproject bound to this project.
 * A subproject is any working repository in the project ecosystem (microservices,
 * frontends, backends, shared libraries, CLI tools, etc.).
 * Runs when topology is enabled (default: true) and auto_discover is true (default: true).
 */
/** Map common file extensions to tree-sitter grammar language IDs. WHY:
 *  Phase 5.2 pre-warms grammars at daemon listen() so the first parse after
 *  cold-start doesn't pay the WASM load cost (~30-80 ms per grammar).
 *  TRA-1540: must cover every grammar in LANG_GRAMMARS — an extension missing
 *  here means its grammar loads lazily on first parse instead of at boot. */
const WARMUP_EXT_TO_LANG: Record<string, string> = {
  ts: 'typescript',
  mts: 'typescript',
  cts: 'typescript',
  tsx: 'tsx',
  js: 'javascript',
  jsx: 'javascript',
  mjs: 'javascript',
  cjs: 'javascript',
  py: 'python',
  pyi: 'python',
  php: 'php',
  rb: 'ruby',
  rake: 'ruby',
  go: 'go',
  rs: 'rust',
  java: 'java',
  kt: 'kotlin',
  kts: 'kotlin',
  swift: 'swift',
  scala: 'scala',
  sc: 'scala',
  cs: 'csharp',
  c: 'c',
  h: 'c',
  cpp: 'cpp',
  cxx: 'cpp',
  cc: 'cpp',
  hpp: 'cpp',
  hxx: 'cpp',
  hh: 'cpp',
  'h++': 'cpp',
  ino: 'cpp',
  pde: 'cpp',
  vue: 'vue',
  ex: 'elixir',
  exs: 'elixir',
  sh: 'bash',
  bash: 'bash',
  zsh: 'bash',
  css: 'css',
  scss: 'css',
  sass: 'css',
  less: 'css',
  styl: 'css',
  stylus: 'css',
  dart: 'dart',
  el: 'elisp',
  elc: 'elisp',
  elm: 'elm',
  erb: 'embedded_template',
  html: 'html',
  htm: 'html',
  json: 'json',
  jsonc: 'json',
  json5: 'json',
  lua: 'lua',
  luau: 'lua',
  m: 'objc',
  mm: 'objc',
  ml: 'ocaml',
  mli: 'ocaml',
  sol: 'solidity',
  toml: 'toml',
  yaml: 'yaml',
  yml: 'yaml',
  zig: 'zig',
  zon: 'zig',
};

/** Probe each managed project's DB once for distinct file extensions and
 *  return the resolved tree-sitter language IDs. Best-effort: a project whose
 *  DB hasn't been opened yet contributes nothing and is silently skipped. */
function collectKnownLanguages(projects: ManagedProject[]): string[] {
  const langs = new Set<string>();
  for (const proj of projects) {
    try {
      const rows = proj.db
        .prepare(
          "SELECT DISTINCT lower(substr(path, length(path) - instr(reverse(path), '.') + 2)) AS ext FROM files LIMIT 100",
        )
        .all() as Array<{ ext: string | null }>;
      for (const row of rows) {
        if (!row.ext) continue;
        const lang = WARMUP_EXT_TO_LANG[row.ext];
        if (lang) langs.add(lang);
      }
    } catch {
      /* DB closed / not ready yet — skip */
    }
  }
  return [...langs];
}

/**
 * Soft GC (TRA-23): prune DB categories that are unambiguous garbage —
 * expired session DBs, and orphan DBs/registry rows whose project root is
 * confirmed gone from disk. Never touches `orphan_unregistered` (DB present
 * but no matching registry row could just be a project not yet re-added) or
 * `stray_small` (small-but-live projects) — those still require the explicit
 * `trace-mcp prune --apply`/`--aggressive` review path. Called at daemon
 * startup and hourly thereafter so ephemeral per-run workdirs (e.g. Multica
 * task workdirs, deleted the moment the run ends) don't accumulate orphaned
 * indexes for the life of a long-running daemon.
 */
function softGcSweep(): void {
  try {
    const summary = pruneIndexDir({
      apply: true,
      sessionTtlDays: 7,
      onlyCategories: ['session_expired', 'orphan_missing_root'],
    });
    const deletedCount = summary.deleted.length;
    if (deletedCount > 0) {
      const freedMb = Math.round(summary.freedBytes / 1_048_576);
      logger.info(
        { deletedFiles: deletedCount, freedMb },
        `Pruned ${deletedCount} expired/orphaned index DBs (${freedMb} MB freed)`,
      );
    }
  } catch (err) {
    logger.warn({ err }, 'pruneIndexDir soft-prune failed (non-fatal)');
  }

  // TRA-702: this used to call `pruneStaleProjects()`, which deregisters a
  // missing root the first time it is seen missing — no grace period. That
  // contradicted the 7-day grace `sweepMissingRoots` applies on the startup
  // path, and the two ran against the same registry an hour apart, so the
  // grace never actually held: a root on an unmounted volume lost its registry
  // row on the next hourly sweep. That in turn made it unregistered, which is
  // half the condition `pruneProjectConfigSections` below uses to delete a
  // config section — so the volume coming back found neither. Use the
  // grace-aware sweep on both paths; `pruneStaleProjects` stays for the
  // explicit `doctor` / `prune --apply` review flows, where immediate is right.
  try {
    const { removed } = sweepMissingRoots(7);
    if (removed.length > 0) {
      logger.info(
        { removed },
        `Deregistered ${removed.length} project(s) with a root missing >7 days`,
      );
    }
  } catch (err) {
    logger.warn({ err }, 'sweepMissingRoots soft-prune failed (non-fatal)');
  }

  // TRA-706: the ceiling that does not depend on recognising a workdir layout.
  // `serve` auto-registers whatever directory it was started in, so a runtime
  // that scatters live scratch checkouts under an unrecognised path shape adds
  // a registry row and a config section per run and neither sweep above
  // reaches them. Runs before the config prune so the rows it drops already
  // read as unclaimed when the section cap below is applied.
  try {
    const evicted = sweepImplicitProjects();
    if (evicted.length > 0) {
      logger.info(
        { evicted: evicted.length },
        `Deregistered ${evicted.length} auto-registered project(s) over the implicit cap`,
      );
    }
  } catch (err) {
    logger.warn({ err }, 'sweepImplicitProjects soft-prune failed (non-fatal)');
  }

  // TRA-702: same job as the registry prune above, on the other registration
  // store. `setupProject` writes a per-project section into .config.json for
  // every root it sets up — ephemeral ones included, because the run that
  // follows reads it back — and nothing collected them, so the file reached
  // 593 sections / 785 KB and was reparsed on every server start. Hourly (not
  // startup-only) is what keeps a long-running daemon's file small, since each
  // agent run adds one section while the daemon stays up.
  try {
    const removedSections = pruneProjectConfigSections();
    if (removedSections.length > 0) {
      logger.info(
        { removedSections: removedSections.length },
        `Pruned ${removedSections.length} dead project section(s) from the global config`,
      );
    }
  } catch (err) {
    logger.warn({ err }, 'pruneProjectConfigSections soft-prune failed (non-fatal)');
  }

  // TRA-702: atomic writes unlink their own tmp on error, but a process killed
  // between open and rename never runs that handler — so every crash leaked one
  // file into the state dir, permanently.
  //
  // TRA-783: the sweep is shallow, and it only ever ran on the state root — a
  // June orphan was still sitting in `sessions/` in September. The full set of
  // atomically-written state directories lives in atomic-write.ts.
  try {
    const removedTmps = sweepOrphanTmpFilesUnderHome(TRACE_MCP_HOME);
    if (removedTmps.length > 0) {
      logger.info(
        { removedTmps: removedTmps.length },
        `Removed ${removedTmps.length} orphaned atomic-write tmp file(s)`,
      );
    }
  } catch (err) {
    logger.warn({ err }, 'sweepOrphanTmpFiles soft-prune failed (non-fatal)');
  }

  // TRA-1218: sweep stale session snapshots, end logs, and orphaned resume
  // summaries in SESSIONS_DIR.
  try {
    const sessionSummary = sweepSessionFiles();
    if (sessionSummary.deleted.length > 0) {
      const freedKb = Math.round(sessionSummary.freedBytes / 1024);
      logger.info(
        { deletedFiles: sessionSummary.deleted.length, freedKb },
        `Pruned ${sessionSummary.deleted.length} stale session file(s) (${freedKb} KB freed)`,
      );
    }
  } catch (err) {
    logger.warn({ err }, 'sweepSessionFiles soft-prune failed (non-fatal)');
  }

  try {
    if (fs.existsSync(TOPOLOGY_DB_PATH)) {
      const topoStore = new TopologyStore(TOPOLOGY_DB_PATH);
      try {
        const res = topoStore.pruneStale();
        if (res.services > 0 || res.subprojects > 0) {
          logger.info(
            { removedServices: res.services, removedSubprojects: res.subprojects },
            `Pruned ${res.services} stale service(s) and ${res.subprojects} stale subproject(s) from topology.db`,
          );
        }
      } finally {
        topoStore.close();
      }
    }
  } catch (err) {
    logger.warn({ err }, 'pruneStaleTopology soft-prune failed (non-fatal)');
  }

  try {
    if (fs.existsSync(DECISIONS_DB_PATH)) {
      const store = new DecisionStore(DECISIONS_DB_PATH);
      try {
        const res = store.pruneStale({ includeMinedSessions: true });
        if (res.decisions > 0 || res.chunks > 0) {
          logger.info(
            { removedDecisions: res.decisions, removedRoots: res.staleRoots },
            `Pruned ${res.decisions} orphaned decision(s) across ${res.staleRoots.length} deleted project root(s)`,
          );
        }
      } finally {
        store.close();
      }
    }
  } catch (err) {
    logger.warn({ err }, 'pruneStaleDecisions soft-prune failed (non-fatal)');
  }

  // TRA-1864: stem-less WAL/SHM/journal sidecars (and snapshots) whose `.db`
  // is gone — the ~1.5 GB legacy pile of pre-TRA-1136 top-level ephemeral
  // DBs, plus any future bare-`unlinkSync` regression. No age gate: a sidecar
  // without its stem DB is never a live run (SQLite creates the main file
  // first), and a live holder vetoes deletion. Runs here so daemon startup
  // and the hourly pass both collect them, not just `prune --apply`.
  try {
    const { deleted, freedBytes } = sweepOrphanDbSidecars();
    if (deleted.length > 0) {
      const freedMb = Math.round(freedBytes / 1_048_576);
      logger.info(
        { deletedFiles: deleted.length, freedMb },
        `Removed ${deleted.length} orphaned index-DB sidecar(s) (${freedMb} MB freed)`,
      );
    }
  } catch (err) {
    logger.warn({ err }, 'sweepOrphanDbSidecars soft-prune failed (non-fatal)');
  }
}

async function runSubprojectAutoSync(projectRoot: string, config: TraceMcpConfig): Promise<void> {
  if (config.topology?.enabled === false) return;
  if (config.topology?.auto_discover === false) return;

  try {
    ensureGlobalDirs();
    const topoStore = new TopologyStore(TOPOLOGY_DB_PATH);
    const manager = new SubprojectManager(topoStore);

    const { services } = await manager.autoDiscoverSubprojects(projectRoot, {
      contractPaths: config.topology?.contract_globs,
    });

    // Also re-link any other previously registered subprojects
    const subprojects = topoStore.getAllSubprojects();
    if (subprojects.length > 1) {
      const linked = topoStore.linkClientCallsToEndpoints();
      if (linked > 0) {
        logger.info({ linked }, 'Subproject: linked additional client calls');
      }
    }

    const totalEndpoints = services.reduce((sum, s) => sum + s.endpoints, 0);
    const totalClientCalls = services.reduce((sum, s) => sum + s.clientCalls, 0);
    logger.info(
      {
        project: projectRoot,
        subprojects: services.length,
        serviceNames: services.map((s) => s.name),
        endpoints: totalEndpoints,
        clientCalls: totalClientCalls,
      },
      'Subproject auto-sync completed',
    );

    topoStore.close();
  } catch (e) {
    logger.warn({ error: e }, 'Subproject auto-sync failed (non-fatal)');
  }
}

const program = new Command();

program
  .name('trace')
  .description('Framework-Aware Code Intelligence for Laravel/Vue/Inertia/Nuxt')
  .version(PKG_VERSION, '-v, --version')
  // Disclose the anonymous ping once, from the place a human is actually
  // looking at a terminal — `trace init` is in every install instruction we
  // publish (TRA-887). Server startup prints the same line as a fallback for
  // installs that never touch the CLI; the shared `noticeShown` flag means
  // whichever runs first is the only one that speaks. Skipped for
  // `classify-env`, which the shell guard runs on every tool call.
  .hook('preAction', (_program, actionCommand) => {
    if (actionCommand.name() !== 'classify-env') printTelemetryNoticeOnce();
  });

program
  .command('serve', { isDefault: true })
  .description('Start MCP server (stdio transport)')
  .option(
    '--preset <name>',
    'Tool preset (e.g. router, minimal, review, dev, security, design, perf, architecture, standard, full)',
  )
  .action(async (opts: { preset?: string } = {}) => {
    if (opts.preset) {
      process.env.TRACE_MCP_PRESET = opts.preset;
    }
    // Keep a stray unhandled rejection / uncaught exception from tearing down
    // the whole MCP session — log it and stay alive instead (see #202-adjacent
    // "disconnects/crashes" reports).
    installProcessSafetyNet('serve');
    // cwd, or TRACE_MCP_REPO_ROOT when a client spawned us somewhere the user
    // never chose. `doctor` reports these same roots (TRA-1087).
    const { projectRoot, indexRoot, worktreeMainRoot, envOverride } = resolveServeRoots();

    // Auto-update + post-update migrations. TRA-703: both run in the background,
    // never on the path to the `initialize` response — awaiting them here made
    // the first session after every release time out and connect as `failed`.
    // Controlled by ~/.trace-mcp/.config.json: { "auto_update": true, "auto_update_check_interval_hours": 24 }
    const globalRaw = loadGlobalConfigRaw();
    scheduleBackgroundUpdate({
      checkIntervalHours:
        typeof globalRaw.auto_update_check_interval_hours === 'number'
          ? globalRaw.auto_update_check_interval_hours
          : 12,
      install: globalRaw.auto_update !== false,
    });

    // Linked git worktrees share the main repo's index instead of building a
    // redundant copy: projectRoot stays the worktree path (file watcher, path
    // validation), the DB comes from the main repo.
    if (worktreeMainRoot) {
      logger.info(
        { worktreeRoot: projectRoot, mainRoot: worktreeMainRoot },
        'Git worktree detected — sharing main repo index',
      );
    }
    if (envOverride) {
      logger.info({ envOverride }, 'Serving TRACE_MCP_REPO_ROOT instead of cwd');
    }

    // Auto-register the index root (main repo if worktree, otherwise current project).
    // Only register if findProjectRoot resolves to indexRoot itself — never climb
    // above CWD, which would accidentally register a parent directory
    // (e.g. ~/PhpstormProjects when CWD is ~/PhpstormProjects/some-project).
    // GH#1371 / TRA-1881: `auto_register.mode: "never"` (or `"ask"`) disables
    // this entirely — serve keeps going with defaults and an honest
    // "not indexed" surface instead of a cold index.
    const existing = getProject(indexRoot);
    if (!existing) {
      try {
        const root = findProjectRoot(indexRoot);
        if (root === path.resolve(indexRoot)) {
          setupProject(root);
          logger.info({ root }, 'Auto-registered project');
        } else {
          logger.debug(
            { cwd: indexRoot, resolvedRoot: root },
            'Skipped auto-register: project root is above CWD',
          );
        }
      } catch (e) {
        // Not a project dir — will still try to serve with defaults.
        // setupProject() also throws here when auto_register.mode disables
        // implicit registration — same outcome, but worth saying plainly.
        const msg = e instanceof Error ? e.message : '';
        if (msg.includes('Auto-registration is disabled')) {
          logger.info({ root: indexRoot }, 'Skipped auto-register: auto_register.mode disables it');
        }
      }
    }

    const configResult = await loadConfig(projectRoot);
    if (configResult.isErr()) {
      logger.error({ error: configResult.error }, 'Failed to load config');
      process.exit(1);
    }
    const config = configResult.value;

    // Attach file logging if configured
    if (config.logging) {
      attachFileLogging(config.logging);
    }

    // ── Stdio session: unified proxy ⇄ full mode with zero-downtime swap ──
    // One long-lived stdio process that hot-swaps between forwarding to the
    // daemon (when alive) and running a local McpServer (when daemon is dead).
    // See src/daemon/router/session.ts for the full state machine.
    const sharedDbPath = resolveDbPath(indexRoot);
    const idleTimeoutMs = (config.idle_timeout_minutes ?? 30) * 60_000;
    const daemonStabilityMs = (config.daemon_stability_seconds ?? 30) * 1_000;
    const drainTimeoutMs = config.backend_swap_drain_ms ?? 5_000;
    // Product default (ROADMAP week 2026-09-07, re-confirmed TRA-1832 sweep
    // 2026-09-22): the daemon stays the default session backend. Rationale:
    // proxy-by-default + snapshot fast path (TRA-948) + hot-swap local
    // fallback kept every session alive through a live restart storm
    // (8 daemon-disappeared flips, 0 user-visible outages — TRA-1836).
    // The storm itself (silent /health under bulk load) is the defect to fix,
    // not a reason to flip the default: flipping would trade one shared
    // daemon for a local backend per session on every busy machine.
    const autoSpawnDaemon =
      process.env.TRACE_MCP_NO_DAEMON === '1' ? false : (config.auto_spawn_daemon ?? true);
    const autoSpawnTimeoutMs = (config.daemon_spawn_timeout_seconds ?? 20) * 1_000;
    // Discoverability (#202): make the daemon opt-out knobs visible. Logged to
    // stderr/file (never stdout — that carries the JSON-RPC stream).
    if (autoSpawnDaemon) {
      logger.info(
        'Daemon auto-spawn is enabled. To run pure stdio: `trace-mcp daemon stop`, ' +
          'or set TRACE_MCP_NO_DAEMON=1 / auto_spawn_daemon=false in ~/.trace-mcp/.config.json.',
      );
    } else {
      logger.info('Daemon auto-spawn is disabled — running local-only (stdio).');
    }

    const session = new StdioSession({
      projectRoot,
      indexRoot,
      config,
      sharedDbPath,
      daemonPort: DEFAULT_DAEMON_PORT,
      idleTimeoutMs,
      daemonStabilityMs,
      drainTimeoutMs,
      autoSpawnDaemon,
      autoSpawnTimeoutMs,
      // See the import comment above: resolves instantly from the reference
      // already loaded by this file, so the snapshot fast path (TRA-948)
      // keeps its <400ms budget.
      loadSnapshotBackend: () => Promise.resolve({ SnapshotBackend }),
    });

    logger.info(
      { projectRoot, indexRoot, idleTimeoutMs, daemonStabilityMs },
      'Starting trace-mcp stdio session...',
    );

    // Soft GC in stdio session: run once asynchronously after startup so we don't
    // delay handshake, but keep .config.json and orphan tmp files clean even when
    // running in standalone stdio mode without the daemon (TRA-1197).
    setImmediate(() => {
      try {
        softGcSweep();
      } catch (err) {
        logger.debug({ err }, 'stdio softGcSweep error');
      }
    });

    // Shared with the thin proxy entry (src/proxy-entry.ts, TRA-970) so both
    // processes wire signals/shutdown and exit identically.
    await runStdioSession(session);
  });

program
  .command('serve-http')
  .description(
    'Start MCP server (HTTP/SSE transport) — daemon mode, indexes all registered projects',
  )
  .option('-p, --port <port>', 'Port to listen on', String(DEFAULT_DAEMON_PORT))
  .option('--host <host>', 'Host to bind to', '127.0.0.1')
  .option(
    '--allow-remote',
    'Permit a non-loopback --host. The daemon is unauthenticated — only use behind your own auth.',
  )
  .option(
    '--preset <name>',
    'Tool preset for clients that connect to this daemon directly over HTTP (e.g. router, minimal, review, dev, security, design, perf, architecture, standard, full). A stdio session carries its own preset and is never narrowed by this.',
  )
  .action(async (opts: { port: string; host: string; allowRemote?: boolean; preset?: string }) => {
    // TRA-1911: install the synchronous exit breadcrumb FIRST, before any early
    // `process.exit()` below. Pino buffers asynchronously, so a log line
    // immediately followed by `process.exit()` can die in the buffer and the
    // death lands in daemon.log as silence. The breadcrumb's `exit` handler is
    // sync (`appendFileSync`) and always leaves one attributable line behind.
    installDaemonExitBreadcrumb();
    // TRA-1807: fail closed when launched from an ephemeral install (agent-run
    // sandbox, shared tmp). A daemon serving from a directory that dies with
    // the run keeps answering /health while parsing + extraction are dead.
    // Dev checkouts (.git above the entry) are deliberate invocations, not a
    // hijacked live daemon, and are exempt — same as the postinstall skip.
    const refusal = ephemeralServeHttpRefusal(process.argv[1] ?? '');
    if (refusal) {
      console.error(refusal);
      noteDaemonShutdownReason('ephemeral-install-refusal');
      process.exit(1);
    }
    if (opts.preset) {
      process.env.TRACE_MCP_PRESET = opts.preset;
    }
    // A single unhandled rejection must not take down the daemon serving every
    // registered project — log and stay alive.
    installProcessSafetyNet('serve-http');
    // Rotate daemon.log from within the long-lived daemon — the spawn-point
    // rotation only fires at restart, so a long-running daemon never rotated it.
    startDaemonLogRotation();
    // TRA-421: say what launchd recorded about the previous run *before* doing
    // anything else, so a crash loop is visible in daemon.log itself rather
    // than only via `launchctl print` after someone thinks to look.
    logPreviousExit();
    // TRA-1807: verify the install tree is intact before serving. A daemon
    // started from a half-deleted tree (or one deleted mid-run by a finished
    // agent sandbox) otherwise degrades silently: every parse fails with
    // ENOENT and the extract pool disables itself. Fail loudly instead.
    // Skipped for dev runs from source (no bundled dist/ there by design).
    if (PKG_VERSION !== '0.0.0-dev') {
      const runtimeProblems = checkDaemonRuntimeIntact(
        path.dirname(fileURLToPath(import.meta.url)),
      );
      if (runtimeProblems.length > 0) {
        for (const problem of runtimeProblems) {
          logger.error({ problem }, 'serve-http startup integrity check failed');
          console.error(`trace serve-http: ${problem}`);
        }
        noteDaemonShutdownReason('startup-integrity-failure');
        process.exit(1);
      }
    }
    // TRA-421 registers our PID so clients can tell "busy" from "dead" without
    // /health. TRA-525: that registration happens in the listen() callback, NOT
    // here. Writing it before bind let a process that never becomes the daemon
    // claim to be one: on `daemon restart` the new process overwrote daemon.pid,
    // lost the port race to the still-running old daemon, and exited — leaving
    // the file naming a dead PID. isDaemonProcessAlive() then read "dead" while
    // a healthy daemon was serving, which disarmed the very guard that exists to
    // stop the restart war (measured: 724 restarts in 18.7h, peak 89/h).
    // Auto-update (same logic as serve). TRA-703: also off the startup path —
    // a daemon stuck in `npm install` never binds its port, so every stdio
    // client that auto-spawned it sits in tryAutoSpawnDaemon until timeout and
    // only then answers `initialize`. The watchdog below picks up the installed
    // version on its next tick and restarts the daemon between sessions.
    const globalRaw = loadGlobalConfigRaw();
    scheduleBackgroundUpdate({
      checkIntervalHours:
        typeof globalRaw.auto_update_check_interval_hours === 'number'
          ? globalRaw.auto_update_check_interval_hours
          : 12,
      install: globalRaw.auto_update !== false,
    });

    // Phase 5.1: defer registered-project loading until AFTER httpServer.listen
    // so the daemon is reachable from the first millisecond. Requests against
    // a still-indexing project get 503 + Retry-After and the hook falls back
    // to the local CLI path transparently. See plan-next-optimizations §5.1.
    const projectManager = new ProjectManager();

    // ── Client tracking ─────────────────────────────────────────
    interface TrackedClient {
      id: string;
      name?: string;
      project: string;
      transport: string;
      connectedAt: string;
      lastSeen: string;
    }
    const clients = new Map<string, TrackedClient>();

    // ── SSE event bus ───────────────────────────────────────────
    // R09 v2 extends this union with pipeline-lifecycle variants
    // (reindex_*, embed_*, snapshot_created). No new event bus is
    // introduced — all variants flow through the existing
    // broadcastEvent() function below. Single source of truth.
    type DaemonEvent =
      | {
          type: 'indexing_progress';
          project: string;
          pipeline: string;
          phase: string;
          processed: number;
          total: number;
        }
      | { type: 'project_status'; project: string; status: string; error?: string }
      | {
          type: 'client_connect';
          clientId: string;
          project: string;
          transport?: string;
          name?: string;
        }
      | { type: 'client_update'; clientId: string; project?: string; name?: string }
      | { type: 'client_disconnect'; clientId: string; project?: string }
      | {
          type: 'journal_entry';
          project: string;
          ts: number;
          tool: string;
          params_summary: string;
          result_count: number;
          result_tokens?: number;
          latency_ms?: number;
          is_error: boolean;
          session_id: string;
        }
      // ── R09 v2: pipeline-lifecycle variants ──────────────────
      | {
          type: 'reindex_started';
          project: string;
          pipeline: string;
          total_files?: number;
        }
      | {
          type: 'reindex_completed';
          project: string;
          pipeline: string;
          duration_ms: number;
          summary?: Record<string, unknown>;
        }
      | {
          type: 'reindex_errored';
          project: string;
          pipeline: string;
          message: string;
        }
      | { type: 'embed_started'; project: string; total?: number }
      | { type: 'embed_progress'; project: string; processed: number; total: number }
      | {
          type: 'embed_completed';
          project: string;
          duration_ms: number;
          embedded: number;
        }
      | {
          type: 'snapshot_created';
          project: string;
          name: string;
          summary?: Record<string, unknown>;
        };

    const sseConnections = new Set<http.ServerResponse>();
    /**
     * Live TCP sockets under httpServer (TRA-1017, cause B). `httpServer.close(cb)`
     * fires its callback only after the last connection ends, so a socket the
     * shutdown did not end wedges the whole graceful path past the deadline.
     * The count is logged at 'Projects stopped' and in the deadline handler —
     * the next field log names the holder instead of us guessing.
     */
    const openSockets = new Set<Socket>();

    // Per-(project, pipeline) timestamps for the 200 ms progress throttle.
    // Terminal events (reindex_completed, reindex_errored, embed_completed,
    // snapshot_created) are never throttled.
    const PROGRESS_THROTTLE_MS = 200;
    const lastProgressEmittedAt = new Map<string, number>();

    function broadcastEvent(event: DaemonEvent): void {
      const now = Date.now();
      // Passive sweep: drop stale throttle keys when the map crosses the soft
      // cap. Cheap (no timer, runs inline at most once per event).
      maybePruneProgressThrottle(lastProgressEmittedAt, now);
      // Throttle floor for high-frequency progress variants. Keyed by
      // (project, pipeline) for indexing_progress and (project, 'embed')
      // for embed_progress. Other variants bypass the floor.
      if (!shouldEmitProgressEvent(lastProgressEmittedAt, event, now, PROGRESS_THROTTLE_MS)) {
        return;
      }
      // Terminal pipeline events: drop the throttle key so finished pipelines
      // do not pin entries inside an active (non-removed) project.
      clearProgressKeyForTerminalEvent(lastProgressEmittedAt, event);
      const data = `data: ${JSON.stringify(event)}\n\n`;
      for (const res of sseConnections) {
        try {
          res.write(data);
        } catch {
          sseConnections.delete(res);
        }
      }
    }

    // Subscribe to progress updates from all managed projects.
    // Keyed by project root so removeProject() can tear down the listener
    // (it otherwise pins the project's ProgressState — and through the
    // listener closure, broadcastEvent + project root — forever).
    const progressUnsubscribers = new Map<string, () => void>();
    function subscribeToProjectProgress(root: string): void {
      const managed = projectManager.getProject(root);
      if (!managed) return;
      // Defensive: drop any prior listener for this root before replacing it
      // (re-add of the same project must not double-subscribe).
      progressUnsubscribers.get(root)?.();
      const unsub = managed.progress.onUpdate((pipeline, progress) => {
        broadcastEvent({
          type: 'indexing_progress',
          project: root,
          pipeline,
          phase: progress.phase,
          processed: progress.processed,
          total: progress.total,
        });
      });
      progressUnsubscribers.set(root, unsub);
    }

    /**
     * Tear down all daemon-side bookkeeping for a removed project.
     * Pairs with `subscribeToProjectProgress` / addProject paths.
     *
     * Thin wrapper around the unit-tested helper in
     * `daemon/project-bookkeeping.ts` — the helper owns no globals; cli.ts
     * passes in the local maps/sets so the same code path is exercised by
     * both the daemon and the unit tests.
     */
    function teardownProjectBookkeeping(root: string): void {
      teardownProjectBookkeepingImpl(root, {
        progressUnsubscribers,
        lastProgressEmittedAt,
        projectSessions,
        sessionTransports,
        sessionHandles,
        sessionClients,
        clients,
        sessionLastSeen,
      });
    }

    // Subscribe to all currently loaded projects
    for (const p of projectManager.listProjects()) {
      subscribeToProjectProgress(p.root);
    }

    // ── Lazy HTTP project loading (TRA-1052) ──────────────────────────────
    // The MCP path (createSessionTransport's caller, above) has always been
    // able to auto-register/reload a project on first connect. The REST API
    // never had an equivalent: every `/api/projects/*` read 404'd the moment
    // `projectManager.getProject()` came back empty, which is the daemon's
    // resident-in-memory set — a strict subset of what's registered on disk
    // (idle-unloaded projects, or ones dropped from memory by a daemon
    // restart, stay registered but aren't in that set). On a real workspace
    // with 40+ registered projects and a handful resident, that made most of
    // the project list open broken (404 banners, a raw "Project not found:
    // <path>" string on Graph, Ask stuck on "Connecting…").
    //
    // One in-flight load per root, shared across concurrent requests for the
    // same project — without this, N tabs/fetches racing a cold project would
    // each fire their own addProject() (redundant DB opens/watcher starts).
    const projectLoadPromises = new Map<string, Promise<void>>();

    type ProjectResolution =
      | { ok: true; managed: ManagedProject }
      | {
          ok: false;
          status: 404 | 503;
          reason: 'not_registered' | 'folder_missing' | 'loading' | 'indexing';
          message: string;
          retryAfterSec?: number;
        };

    /**
     * Resolve a REST request's `?project=` to a resident `ManagedProject`,
     * kicking off a background reload when it's registered but currently
     * unloaded — mirrors the MCP auto-register path. Never throws; callers
     * turn a non-ok result into a response via `writeProjectResolutionError`.
     *
     * `requireReady`: for routes that need a fully-indexed project (Ask),
     * a resident-but-still-indexing project also answers "not ready yet"
     * rather than racing a half-built index.
     */
    function resolveProjectForRest(
      root: string,
      opts?: { requireReady?: boolean },
    ): ProjectResolution {
      const managed = projectManager.getProject(root);
      if (managed) {
        if (opts?.requireReady && managed.status !== 'ready') {
          return {
            ok: false,
            status: 503,
            reason: 'indexing',
            message: `"${path.basename(root)}" is still indexing — try again in a few seconds.`,
            retryAfterSec: 3,
          };
        }
        return { ok: true, managed };
      }

      const registryEntry = getProject(root);
      if (!registryEntry) {
        return {
          ok: false,
          status: 404,
          reason: 'not_registered',
          message: `"${path.basename(root)}" isn't registered with this daemon. Add it from the app's project list.`,
        };
      }
      if (!fs.existsSync(root)) {
        return {
          ok: false,
          status: 404,
          reason: 'folder_missing',
          message: `"${path.basename(root)}"'s folder no longer exists on disk. Remove it from the project list.`,
        };
      }

      // Registered but idle-unloaded (or dropped by a daemon restart) — kick
      // off (or join) a reload. `addProject()` itself is idempotent (returns
      // the existing entry if one raced in first), but starting it twice from
      // here would still pay setup cost twice, hence the shared promise map.
      if (!projectLoadPromises.has(root)) {
        const promise = projectManager
          .addProject(root)
          .then(() => {
            subscribeToProjectProgress(root);
            broadcastEvent({ type: 'project_status', project: root, status: 'indexing' });
          })
          .catch((err) => {
            logger.warn(
              { err: String(err), projectRoot: root },
              'Lazy reload for REST request failed',
            );
          })
          .finally(() => {
            projectLoadPromises.delete(root);
          });
        projectLoadPromises.set(root, promise);
      }
      return {
        ok: false,
        status: 503,
        reason: 'loading',
        message: `Loading "${path.basename(root)}"…`,
        retryAfterSec: 5,
      };
    }

    /** Write a non-ok `ProjectResolution` as the HTTP response. */
    function writeProjectResolutionError(
      res: http.ServerResponse,
      resolution: Extract<ProjectResolution, { ok: false }>,
      contentType: 'json' | 'text' = 'json',
    ): void {
      const headers: Record<string, string> = {
        'Content-Type': contentType === 'json' ? 'application/json' : 'text/plain',
      };
      if (resolution.retryAfterSec != null) {
        headers['Retry-After'] = String(resolution.retryAfterSec);
      }
      res.writeHead(resolution.status, headers);
      res.end(
        contentType === 'json'
          ? JSON.stringify({ error: resolution.message, reason: resolution.reason })
          : resolution.message,
      );
    }

    // Shared project-level resources (TopologyStore, DecisionStore) — avoids per-session SQLite overhead
    const { ProjectResourcePool } = await import('./daemon/resource-pool.js');
    const resourcePool = new ProjectResourcePool();
    // Wire the pool into ProjectManager so stopProject/removeProject force-
    // disposes the project's pool entry (two SQLite handles otherwise leak per
    // removed project for the daemon's lifetime).
    projectManager.setResourcePool(resourcePool);
    // Cross-project relay for call_project_tool — additive wiring on top of
    // the existing ProjectManager/ProjectResourcePool, not a second pool.
    const projectRelay = createDaemonProjectRelay(projectManager, resourcePool);
    // Branch indexes for linked worktrees (GH #1481 step 2): a copy of the
    // canonical index with the worktree delta re-indexed into it, served to
    // worktree sessions. Main-checkout sessions never touch it.
    const worktreeIndexManager = new WorktreeIndexManager({
      settings: resolveWorktreeIndexSettings(globalRaw.worktree_index),
      getCanonical: (root) => projectManager.getProject(root),
      getExtractPool: (config) => projectManager.getSharedExtractPool(config),
      dropPoolRoot: (root) => projectManager.dropSharedPoolRoot(root),
      sharedServerDeps: (config) => resourcePool.getSharedDeps(config),
      version: PKG_VERSION,
    });
    worktreeIndexManager.start();

    // Per-session MCP transports: sessionId → transport
    // Multiple clients can connect to the same project simultaneously.
    const sessionTransports = new Map<string, StreamableHTTPServerTransport>();
    // Session handles for cleanup: sessionId → ServerHandle
    const sessionHandles = new Map<string, import('./server/server.js').ServerHandle>();
    // Session → client tracking: sessionId → clientId
    const sessionClients = new Map<string, string>();
    // Reverse lookup: projectRoot → Set<sessionId> (for cleanup)
    const projectSessions = new Map<string, Set<string>>();
    // Per-session last-traffic timestamps (sessionId → epoch ms) for the
    // stale-session sweep (TRA-1627). Set when a session is stored after
    // initialize and refreshed on every /mcp request carrying a known
    // session id; removed by the session's onclose cleanup. Without this,
    // a client that dies without DELETE /mcp (kill -9, OOM, `runtime went
    // offline`) pins its project resident forever: resourcePool.acquire()
    // in createSessionTransport is balanced only by transport.onclose,
    // which a dead client never triggers.
    const sessionLastSeen = new Map<string, number>();

    // Durable activity log. Journals die with their session — every agent run
    // is a client that disconnects — so the Activity tab read zero on every
    // window regardless of how busy the project was (TRA-1071). Entries are
    // appended here as they are broadcast and read back per time window.
    let activityStore: import('./session/activity-store.js').ActivityStore | null = null;
    let stopActivityWatchdog: (() => void) | null = null;
    // Last time a journal broadcast actually reached the store. Distinct from
    // the store's own flush health: this answers "is anything emitting",
    // the watchdog answers "is the store writing what it receives".
    let lastJournalEmitAt = 0;
    try {
      const { ActivityStore } = await import('./session/activity-store.js');
      activityStore = new ActivityStore();
      stopActivityWatchdog = activityStore.startWatchdog();
    } catch (e) {
      logger.warn(`activity store unavailable: ${(e as Error)?.message ?? e}`);
    }
    // TRA-1868 self-check: the journal went silently blind for 2+ hours
    // across healthy daemon restarts (no warn anywhere) while tool traffic
    // kept flowing. Every 5 min, if live sessions recorded tool calls
    // recently but nothing reached the durable journal for 15+ min, say so
    // in daemon.log instead of letting the next night-QA run go blind.
    const journalSilenceCheck = setInterval(
      () => {
        try {
          if (!activityStore || lastJournalEmitAt === 0) return;
          const now = Date.now();
          if (now - lastJournalEmitAt <= 15 * 60 * 1000) return;
          let liveTraffic = false;
          for (const h of sessionHandles.values()) {
            try {
              if (h.journal.latestTimestamp() > now - 5 * 60 * 1000) {
                liveTraffic = true;
                break;
              }
            } catch {
              /* a half-torn-down session must not break the sweep */
            }
          }
          if (liveTraffic) {
            logger.warn(
              {
                silentMs: now - lastJournalEmitAt,
                sessions: sessionHandles.size,
              },
              'activity journal silent 15+ min despite live tool traffic — Activity history and is_error metrics have gaps (TRA-1868)',
            );
            // One line per stuck episode, not per tick: without traffic the
            // next tick stays quiet by itself, but with CONTINUED traffic the
            // emit timestamp would have moved. Re-arm by pretending we just
            // warned "now" — the next warn fires only after another 15 min of
            // traffic-without-emits.
            lastJournalEmitAt = now;
          }
        } catch {
          /* self-check must never break the daemon */
        }
      },
      5 * 60 * 1000,
    );
    journalSilenceCheck.unref?.();

    async function createSessionTransport(
      projectRoot: string,
      /**
       * Serve every tool regardless of the daemon's own `tools.preset`
       * (TRA-951). Set for stdio sessions, which carry their own preset and
       * apply it client-side in ProxyBackend. A client connecting to `/mcp`
       * directly has no such filter, so it keeps the daemon's preset.
       */
      fullSurface: boolean,
      /** The preset the client asked for, when it sent one (TRA-951). */
      clientPreset: string | undefined,
      /** Linked worktree the stdio proxy routed to this canonical project, if any. */
      worktreeHint?: string,
    ): Promise<StreamableHTTPServerTransport | null> {
      const managed = projectManager.getProject(projectRoot);
      if (!managed) return null;

      // Pre-allocate the session id so the journal callback below can stamp
      // each emitted event with the same id the MCP transport will use.
      const sessionId = randomUUID();
      const transport = stripRedundantSchemaKeyword(
        new StreamableHTTPServerTransport({
          sessionIdGenerator: () => sessionId,
        }),
      );

      // Each session needs its own Server instance since the MCP SDK's Server
      // only supports one transport at a time. All sessions share the same
      // underlying Store/Pipeline/Registry via the managed project.
      // TopologyStore and DecisionStore are shared via resource pool.
      // `managed.config` was loaded once, when the daemon first added this
      // project, and is never refreshed — so a session would be served a tool
      // surface built from whatever the config said back then. Everything
      // `tools.*` shapes (description_verbosity, instructions_verbosity,
      // descriptions, compact_schemas, meta_fields, agent_behavior) is baked
      // into createServer(), so the stale copy silently ignored the user's
      // config (TRA-373). Re-read it per session; fall back to the cached copy
      // if the file is unreadable or invalid.
      const freshConfig = await loadConfig(projectRoot);
      const sessionConfig = freshConfig.isOk() ? freshConfig.value : managed.config;

      const baseDeps = resourcePool.getSharedDeps(managed.config);
      const deps = {
        ...baseDeps,
        sessionId,
        onJournalEntry: (
          data: import('./server/journal-broadcast.js').JournalEntryCallbackData,
        ) => {
          const entry = { ...data, project: projectRoot, session_id: sessionId };
          broadcastEvent(buildJournalEvent(entry));
          activityStore?.record(entry);
          lastJournalEmitAt = Date.now();
        },
        // R09 v2: lets MCP tools (embed_repo, snapshot_graph) emit
        // pipeline-lifecycle events through the existing SSE bus.
        // Tagged with the project root so the renderer can filter.
        onPipelineEvent: (event: import('./server/server.js').PipelineLifecycleEvent) => {
          broadcastEvent({ ...event, project: projectRoot } as DaemonEvent);
        },
        transport: 'http' as const,
        projectRelay,
        // TRA-951: the daemon advertises the full surface to a session that
        // applies its own preset client-side (ProxyBackend).
        serveFullSurface: fullSurface,
        sessionPreset: clientPreset,
        worktreeRoot: worktreeHint,
        // Null unless the hint names a linked worktree of this checkout and the
        // feature is on; starts building that worktree's branch index.
        worktreeIndex: worktreeIndexManager.routeFor(managed.root, worktreeHint) ?? undefined,
      };
      const handle = createServer(
        managed.store,
        managed.registry,
        sessionConfig,
        managed.root,
        managed.progress,
        deps,
      );
      await handle.server.connect(transport);

      // Count the session only after the fallible setup above succeeded.
      // An acquire() before a throwing createServer/connect would leak: the
      // session never reaches the /mcp handler, so neither onclose (never
      // chained or never fired) nor the idle sweep (no map entry) could
      // ever release it (TRA-1627 review). Everything below is map sets +
      // broadcast (effectively non-throwing), matching the post-store
      // reasoning in the /mcp handler.
      resourcePool.acquire(projectRoot, managed.config);

      // Track client connection
      const clientId = randomUUID();
      clients.set(clientId, {
        id: clientId,
        project: projectRoot,
        transport: 'http',
        connectedAt: new Date().toISOString(),
        lastSeen: new Date().toISOString(),
      });
      broadcastEvent({ type: 'client_connect', clientId, project: projectRoot });

      // Preserve the onclose handler wired by Protocol.connect() above — overwriting
      // it would skip Protocol's own state cleanup. Chain ours after it.
      let cleanedUp = false;
      const cleanupSessionResources = () => {
        if (cleanedUp) return;
        cleanedUp = true;

        const sid = transport.sessionId || sessionId;
        sessionTransports.delete(sid);
        projectSessions.get(projectRoot)?.delete(sid);
        sessionLastSeen.delete(sid);

        const h =
          sessionHandles.get(sid) ??
          (transport as unknown as { __pendingHandle?: import('./server/server.js').ServerHandle })
            .__pendingHandle;
        if (h) {
          try {
            h.dispose();
          } catch {
            /* best-effort */
          }
          sessionHandles.delete(sid);
          (
            transport as unknown as { __pendingHandle?: import('./server/server.js').ServerHandle }
          ).__pendingHandle = undefined;
        }

        const cid =
          sessionClients.get(sid) ??
          (transport as unknown as { __pendingClientId?: string }).__pendingClientId ??
          clientId;
        if (cid) {
          if (clients.has(cid)) {
            clients.delete(cid);
            broadcastEvent({ type: 'client_disconnect', clientId: cid, project: projectRoot });
          }
          sessionClients.delete(sid);
          (transport as unknown as { __pendingClientId?: string }).__pendingClientId = undefined;
        }

        // Release shared resources ref
        resourcePool.release(projectRoot);
        pokeActivity(projectRoot);
      };

      const protocolOnClose = transport.onclose;
      transport.onclose = () => {
        protocolOnClose?.();
        cleanupSessionResources();
      };

      // Store handle and client mapping (will be registered after session ID is assigned)
      // The session ID is set by handleRequest during initialize, so we store by transport ref
      // and move to sessionHandles map after the initialize response.
      (
        transport as unknown as { __pendingHandle?: import('./server/server.js').ServerHandle }
      ).__pendingHandle = handle;
      (transport as unknown as { __pendingClientId?: string }).__pendingClientId = clientId;

      return transport;
    }

    function getTransportBySessionId(sessionId: string): StreamableHTTPServerTransport | undefined {
      return sessionTransports.get(sessionId);
    }

    const port = parseInt(opts.port, 10);
    const host = opts.host;

    // TRA-301: the daemon has no authentication, so loopback IS the trust
    // boundary. Widening it must be deliberate.
    const bindRefusal = checkBindHost(host, opts.allowRemote === true);
    if (bindRefusal) {
      logger.error({ host, port }, bindRefusal);
      process.stderr.write(`${bindRefusal}\n`);
      // TRA-2037: without this the refusal is a silent death — the pino line
      // above is buffered and `process.exit()` can discard it.
      noteDaemonShutdownReason('bind-host-refusal');
      process.exit(1);
    }
    if (!isLoopbackHost(host)) {
      logger.warn(
        { host, port },
        'Daemon bound to a non-loopback host with --allow-remote. There is NO authentication ' +
          'on /mcp or /api: any reachable client can index and read arbitrary directories.',
      );
    }

    // Simple per-IP rate limiter (token bucket). Localhost is exempt because
    // the Electron app and local tooling legitimately make bursts of requests
    // (graph refetch, theme switching, etc.) that would otherwise trip the limit.
    const RATE_WINDOW_MS = 60_000;
    const RATE_LIMIT = 2000;
    const MAX_RATE_BUCKETS = 10_000;
    const rateBuckets = new Map<string, { count: number; resetAt: number }>();

    const LOCALHOST_IPS = new Set(['127.0.0.1', '::1', '::ffff:127.0.0.1', 'localhost', 'unknown']);
    function isLocalhost(ip: string): boolean {
      return LOCALHOST_IPS.has(ip);
    }

    function isRateLimited(ip: string): boolean {
      if (isLocalhost(ip)) return false;
      const now = Date.now();
      const bucket = rateBuckets.get(ip);
      if (!bucket || now > bucket.resetAt) {
        if (!bucket && rateBuckets.size >= MAX_RATE_BUCKETS) {
          for (const [key, b] of rateBuckets) {
            if (now > b.resetAt) rateBuckets.delete(key);
          }
          if (rateBuckets.size >= MAX_RATE_BUCKETS) return true;
        }
        rateBuckets.set(ip, { count: 1, resetAt: now + RATE_WINDOW_MS });
        return false;
      }
      bucket.count++;
      return bucket.count > RATE_LIMIT;
    }

    const rateBucketCleanup = setInterval(() => {
      const now = Date.now();
      for (const [ip, bucket] of rateBuckets) {
        if (now > bucket.resetAt) rateBuckets.delete(ip);
      }
    }, RATE_WINDOW_MS);
    rateBucketCleanup.unref();

    // ── Stale client sweep ───────────────────────────────────────
    // If a stdio-proxy crashes without DELETE /api/clients (kill -9, OOM)
    // and reconnects under a new id, the original `clients` entry never gets
    // cleaned up — onclose only fires for HTTP transports. Sweep entries with
    // lastSeen > 1h on a 10-minute timer. HTTP sessions update lastSeen via
    // PATCH /api/clients during MCP initialize; stdio clients update through
    // the same path. If lastSeen hasn't moved in an hour the proxy is gone.
    const CLIENT_STALE_MS = 60 * 60 * 1000;
    const CLIENT_SWEEP_INTERVAL_MS = 10 * 60 * 1000;
    // MCP sessions go stale the same way, with worse consequences (TRA-1627):
    // a dead session's resourcePool refcount pins its whole project resident,
    // defeating the idle-unload sweep. Same 1h threshold — a live client with
    // an hour of zero traffic re-initializes transparently (ProxyBackend
    // replays its cached initialize frame on 404; direct clients follow the
    // spec's reinitialize path our 404 names).
    const SESSION_IDLE_MS = 60 * 60 * 1000;
    const clientSweep = setInterval(() => {
      const now = Date.now();
      const cutoff = now - CLIENT_STALE_MS;
      for (const [id, client] of clients) {
        const seen = Date.parse(client.lastSeen);
        if (Number.isFinite(seen) && seen < cutoff) {
          clients.delete(id);
          try {
            broadcastEvent({ type: 'client_disconnect', clientId: id, project: client.project });
          } catch {
            /* best-effort */
          }
        }
      }
      void (async () => {
        let reaped = 0;
        for (const sid of collectIdleSessions(sessionLastSeen, now, SESSION_IDLE_MS)) {
          const transport = sessionTransports.get(sid);
          if (transport) {
            // close() fires the chained onclose → full cleanup including
            // the resourcePool.release() that balances createSessionTransport.
            try {
              await transport.close();
            } catch {
              /* best-effort */
            }
          }
          // Defensive: onclose removes the maps itself, but a close() no-op
          // (or an orphan without a transport) must not leave stragglers.
          dropSessionBookkeeping(sid, {
            projectSessions,
            sessionTransports,
            sessionHandles,
            sessionClients,
            clients,
            sessionLastSeen,
          });
          reaped++;
        }
        if (reaped > 0) {
          logger.info({ reaped }, 'Reaped idle MCP sessions with no traffic');
        }
      })();
    }, CLIENT_SWEEP_INTERVAL_MS);
    clientSweep.unref();

    const MAX_BODY_SIZE = 5 * 1024 * 1024;

    function collectBody(req: http.IncomingMessage): Promise<Buffer> {
      return new Promise((resolve, reject) => {
        const chunks: Buffer[] = [];
        let size = 0;
        req.on('data', (chunk: Buffer) => {
          size += chunk.length;
          if (size > MAX_BODY_SIZE) {
            req.destroy();
            reject(new Error('BODY_TOO_LARGE'));
          } else {
            chunks.push(chunk);
          }
        });
        req.on('end', () => resolve(Buffer.concat(chunks)));
        req.on('error', reject);
      });
    }

    const startedAt = Date.now();

    // #237: the listener binds BEFORE startup indexing (Phase 5.1 defers
    // loadAllRegistered to the listen() callback), so /health answers from the
    // first millisecond. But during the initial reindex of all registered
    // projects the daemon is alive yet not warmed. Report status "starting"
    // until that finishes so clients wait/back off instead of concluding the
    // daemon is dead and respawning it (the restart-war root cause). Flipped to
    // true at the end of the post-listen bootstrap below.
    let startupComplete = false;

    const httpServer = http.createServer(async (req, res) => {
      const clientIp = req.socket.remoteAddress ?? 'unknown';

      // CORS: allow Electron renderer (and local dev) to read custom headers
      res.setHeader('Access-Control-Allow-Origin', '*');
      res.setHeader(
        'Access-Control-Expose-Headers',
        'X-Graph-Nodes, X-Graph-Edges, X-Graph-Communities',
      );
      if (req.method === 'OPTIONS') {
        res.setHeader('Access-Control-Allow-Methods', 'GET, POST, DELETE, OPTIONS');
        res.setHeader('Access-Control-Allow-Headers', 'Content-Type');
        res.writeHead(204);
        res.end();
        return;
      }

      if (isRateLimited(clientIp)) {
        res.writeHead(429, { 'Content-Type': 'application/json', 'Retry-After': '60' });
        res.end(JSON.stringify({ error: 'Too many requests' }));
        return;
      }

      const url = new URL(req.url ?? '/', `http://${req.headers.host ?? 'localhost'}`);

      // MCP endpoint — route by session ID, create new session on initialize.
      //
      // Project resolution precedence (see daemon/mcp-project-router.ts):
      //   1. `?project=` query param.
      //   2. `X-Trace-Project` HTTP header (set by our stdio-proxy as
      //      belt-and-braces against query-string-stripping intermediaries).
      //   3. `params._meta["traceMcp/projectRoot"]` in the JSON-RPC body —
      //      the documented hook for IDE HTTP-MCP integrations.
      //   4. Initialize body: `clientInfo.name` matched against the
      //      registered-client tracking table (unique-match only).
      //   5. Exactly one registered project — use it.
      //   6. Otherwise: 400 (multi) or 404 (none). NEVER silently fall back
      //      to listProjects()[0] — that was the pre-58e25a2 bug.
      if (url.pathname === '/mcp') {
        const projects = projectManager.listProjects();

        // Parse the POST body upfront — the resolver needs to peek at it
        // before the multi-project decision.
        let parsedBody: unknown;
        if (req.method === 'POST') {
          try {
            const body = await collectBody(req);
            if (body.length > 0) {
              parsedBody = JSON.parse(body.toString());
            }
          } catch {
            // Malformed body — let the inner transport handler surface a
            // proper JSON-RPC parse error after project resolution.
          }
        }

        const headerHint = req.headers['x-trace-project'];
        const headerProject = Array.isArray(headerHint) ? headerHint[0] : headerHint;

        const resolution = resolveProjectForMcpRequest({
          queryProject: url.searchParams.get('project'),
          headerProject,
          body: parsedBody,
          projects,
          trackedClients: [...clients.values()],
          isInitializeRequest,
        });

        // JSON-RPC id from the body, if any — MCP clients otherwise wrap our
        // 404/400 as "-32603 Backend send failed: <raw text>" because a bare
        // {error: '...'} body isn't a valid JSON-RPC response. (issue #168)
        const rpcId = extractRpcId(parsedBody);

        if (resolution.ignoredDangerousHint) {
          const { projectRoot: hinted, reason, via } = resolution.ignoredDangerousHint;
          logger.warn(
            { hinted, reason, via, resolvedAs: resolution.kind },
            'Ignored dangerous project hint from MCP client — resolved without it (TRA-286)',
          );
        }
        if (resolution.kind === 'no-projects' || resolution.kind === 'ambiguous') {
          // TRA-1791: no project to attribute this failure to, so no
          // per-project status sentinel can record it — persist it in the
          // daemon-global snapshot instead. The guard hook reads it to tell
          // "this session cannot consult trace-mcp" apart from "the agent
          // hasn't consulted yet" and stops deadlocking Reads in the former
          // case. Never throws; request handling must not depend on it.
          recordUnresolvableResolution(resolution);
          const { status, body } = buildResolutionFailureError(resolution, rpcId);
          res.writeHead(status, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify(body));
          return;
        }
        if (resolution.via === 'tracked-client') {
          logger.info(
            { projectRoot: resolution.projectRoot },
            'Recovered project from tracked client clientInfo.name',
          );
        }
        const requestedRoot = resolution.projectRoot;
        // TRA-951: only the stdio proxy sends this, and only when it filters
        // the surface itself.
        const fullSurface = url.searchParams.get('surface') === 'full';
        const clientPreset = url.searchParams.get('preset') ?? undefined;
        // Sent by a stdio proxy that routed a linked worktree to this canonical
        // project; lets the session flag results for files the branch changed.
        const worktreeHint = url.searchParams.get('worktree') ?? undefined;

        // Resolve to the deepest KNOWN root: a registered subproject
        // (the/fair/fair-front) wins over its container ancestor (the) so the
        // session serves the subproject's own scoped index instead of the
        // container's mixed blob (#209). Falls back to the raw requested root
        // when nothing covers it — the initialize path below then auto-registers
        // it if it has root markers (reusing any existing getDbPath index).
        const projectRoot = (await resolveDeepestKnownRoot(requestedRoot)) ?? requestedRoot;

        // Route by session ID for existing sessions. `transport` /
        // `createdTransport` live outside the try so the catch can close a
        // freshly minted session that never became usable (TRA-1627).
        const sessionId = req.headers['mcp-session-id'] as string | undefined;
        let transport: StreamableHTTPServerTransport | undefined;
        // Transport minted below in this request: not yet usable by any
        // client, so a failure before it is stored must close it (TRA-1627).
        let createdTransport: StreamableHTTPServerTransport | undefined;
        try {
          if (sessionId) {
            transport = getTransportBySessionId(sessionId);
            if (!transport) {
              res.writeHead(404, { 'Content-Type': 'application/json' });
              res.end(
                JSON.stringify({
                  jsonrpc: '2.0',
                  error: { code: -32000, message: 'Session not found' },
                  id: null,
                }),
              );
              return;
            }
            // Any traffic on a known session proves the client alive — this
            // timestamp is what the stale-session sweep reaps on (TRA-1627).
            // Deliberately NOT pokeActivity here: project idleness is gated
            // by the session refcount while pinned, and per-request pokes
            // would keep a chatty-but-idle project resident forever.
            sessionLastSeen.set(sessionId, Date.now());
          } else if (req.method === 'POST' && isInitializeRequest(parsedBody)) {
            // New session: create transport + server
            transport =
              (await createSessionTransport(
                projectRoot,
                fullSurface,
                clientPreset,
                worktreeHint,
              )) ?? undefined;
            createdTransport = transport;
            if (!transport) {
              // Auto-register the project on first MCP connect when the path
              // is plausibly a real project root. This recovers the common
              // case where the daemon was respawned after auto-update and
              // lost its in-memory project list while a stdio session is
              // asking for a perfectly valid project root (or umbrella
              // workspace containing nested projects).
              //
              // Track the rejection reason so the 404 explains why — issue #168:
              // bare "Project not found" left users guessing whether their
              // MCP client was misconfigured (HOME/system root) or whether the
              // folder had been deleted.
              const folderMissing = !fs.existsSync(projectRoot);
              const dangerReason = folderMissing ? null : isDangerousProjectRoot(projectRoot);
              // Only auto-register a directory that is ITSELF a project (has its
              // own root markers). A bare parent that merely *contains* projects
              // (e.g. ~/PhpstormProjects, ~/dev) must NOT be auto-registered:
              // doing so indexes every nested repo as one giant blob and pins the
              // daemon in permanent re-index churn (issue #209). Real monorepo
              // roots carry their own package.json/go.mod/etc and still qualify;
              // bare container folders fall through to a clean 404 instead.
              const hasMarkers = !folderMissing && hasRootMarkers(projectRoot);
              // A user-registered subproject (topology.db) is trusted even when
              // it carries no generic root markers — the user already declared
              // it a project. It is served READ-MOSTLY: indexed once, no fs
              // watcher, no registry.json entry, so an umbrella repo with many
              // subprojects doesn't spawn N watchers or get restored as N full
              // watched projects on restart (#209). A registry project always
              // wins (full watched mode).
              const asSubproject =
                !folderMissing &&
                !getProject(projectRoot) &&
                (await isKnownSubproject(projectRoot));
              // A root present in the on-disk registry but absent from the
              // manager was idle-unloaded by the sweep
              // (project_idle_unload_minutes) — always eligible for lazy
              // reload, even without generic root markers (the user already
              // vetted it at registration time).
              const isRegistered = !folderMissing && !!getProject(projectRoot);
              const canAutoAdd =
                !folderMissing && !dangerReason && (hasMarkers || asSubproject || isRegistered);
              if (canAutoAdd && !projectManager.getProject(projectRoot)) {
                try {
                  await projectManager.addProject(
                    projectRoot,
                    asSubproject ? { watch: false, persist: false } : undefined,
                  );
                  subscribeToProjectProgress(projectRoot);
                  broadcastEvent({
                    type: 'project_status',
                    project: projectRoot,
                    status: 'indexing',
                  });
                  logger.info(
                    { projectRoot, readMostly: asSubproject },
                    asSubproject
                      ? 'Auto-served registered subproject read-mostly on first MCP connect'
                      : 'Auto-registered project on first MCP connect — nobody ran `add`/`init` ' +
                          `here; undo with \`trace-mcp remove ${projectRoot}\` (#936)`,
                  );
                  transport =
                    (await createSessionTransport(
                      projectRoot,
                      fullSurface,
                      clientPreset,
                      worktreeHint,
                    )) ?? undefined;
                  createdTransport = transport;
                } catch (err) {
                  logger.warn(
                    { err: String(err), projectRoot },
                    'Auto-register on MCP connect failed',
                  );
                }
              }
              if (!transport) {
                res.writeHead(404, { 'Content-Type': 'application/json' });
                res.end(
                  JSON.stringify(
                    buildProjectNotFoundError({
                      projectRoot,
                      folderMissing,
                      dangerReason,
                      hasMarkers,
                      rpcId: extractRpcId(parsedBody),
                    }),
                  ),
                );
                return;
              }
            }
          } else {
            // No session ID and not an initialize. This most commonly happens
            // when the daemon restarted and a client is still trying to reuse
            // its in-memory state. Return 404 with a clear "reinitialize"
            // message so MCP clients that follow the spec's recovery path
            // (re-run `initialize`) can do so automatically. Previously we
            // returned 400 "Missing session ID" which some clients treat as
            // a hard protocol error rather than recoverable session loss.
            res.writeHead(404, { 'Content-Type': 'application/json' });
            res.end(
              JSON.stringify({
                jsonrpc: '2.0',
                error: { code: -32000, message: 'Session expired, reinitialize required' },
                id: null,
              }),
            );
            return;
          }

          await transport.handleRequest(req, res, parsedBody);

          // After handling initialize, store the transport by its new session ID
          if (isInitializeRequest(parsedBody) && transport.sessionId) {
            const sid = transport.sessionId;
            sessionTransports.set(sid, transport);
            sessionLastSeen.set(sid, Date.now());
            if (!projectSessions.has(projectRoot)) {
              projectSessions.set(projectRoot, new Set());
            }
            projectSessions.get(projectRoot)!.add(sid);
            pokeActivity(projectRoot);

            // Move pending handle/clientId to session-keyed maps
            const pendingHandle = (
              transport as unknown as {
                __pendingHandle?: import('./server/server.js').ServerHandle;
              }
            ).__pendingHandle;
            const pendingClientId = (transport as unknown as { __pendingClientId?: string })
              .__pendingClientId;
            if (pendingHandle) {
              sessionHandles.set(sid, pendingHandle);
              (
                transport as unknown as {
                  __pendingHandle?: import('./server/server.js').ServerHandle;
                }
              ).__pendingHandle = undefined;
            }
            if (pendingClientId) {
              sessionClients.set(sid, pendingClientId);
              (transport as unknown as { __pendingClientId?: string }).__pendingClientId =
                undefined;
            }
          }
        } catch (e) {
          if (createdTransport) {
            // The freshly minted session never became usable (initialize
            // failed or the client vanished mid-handshake). Nobody holds
            // this transport, so onclose can never fire for it — close it
            // now so its resourcePool.acquire() is released (TRA-1627).
            // Idempotent with the normal path via the cleanedUp guard.
            try {
              await createdTransport.close();
            } catch {
              /* best-effort */
            }
          }
          if ((e as Error & { stack?: string })?.message === 'BODY_TOO_LARGE') {
            if (!res.headersSent) {
              res.writeHead(413, { 'Content-Type': 'application/json' });
              res.end(JSON.stringify({ error: 'Request body too large' }));
            }
            return;
          }
          logger.error({ error: e }, 'MCP request handling failed');
          if (!res.headersSent) {
            res.writeHead(500, { 'Content-Type': 'application/json' });
            res.end(JSON.stringify({ error: 'Internal server error' }));
          }
        }
        return;
      }

      // Health endpoint — includes project status.
      // TRA-1996: this endpoint reports *residency* (in-memory truth for
      // monitoring/autopilots), while `GET /api/projects` reports
      // *index-readiness* (last-known `ready` for idle-unloaded roots, so the
      // app's CTA reads "Reindex" — TRA-1052). The `status` values therefore
      // intentionally differ for the same root; correlate via `resident`.
      if (req.method === 'GET' && url.pathname === '/health') {
        const loadedRoots = new Set<string>();
        const loaded: Array<{
          root: string;
          status: ManagedProject['status'] | 'unloaded' | 'missing';
          resident: boolean;
        }> = projectManager.listProjects().map((p) => {
          loadedRoots.add(p.root);
          return { root: p.root, status: p.status, resident: true };
        });
        // TRA-1715: a loaded project whose root vanished (deleted task
        // workdir, unmounted volume) must never keep advertising its last
        // live status — no FSEvents drop may ever fire to trigger the
        // rescan guard, so overlay here on every poll (one stat per root).
        const projects = withMissingRoots(loaded, fs.existsSync);
        // Registered projects idle-unloaded by the sweep (project_idle_unload_minutes)
        // are absent from listProjects() — surface them as 'unloaded' rather than
        // silently dropping them, so /health still reflects every registered root.
        for (const entry of listProjects()) {
          if (!loadedRoots.has(entry.root)) {
            projects.push({ root: entry.root, status: 'unloaded', resident: false });
          }
        }
        res.writeHead(200, { 'Content-Type': 'application/json' });
        res.end(
          JSON.stringify(
            buildHealthPayload({
              startupComplete,
              version: PKG_VERSION,
              uptimeSeconds: Math.floor((Date.now() - startedAt) / 1000),
              pid: process.pid,
              projects,
            }),
          ),
        );
        return;
      }

      // REST API: search symbols in a project
      // Schema: symbols(id, file_id, symbol_id, name, kind, fqn, line_start, line_end, ...)
      //         nodes(id, node_type, ref_id) — ref_id points to symbols.id when node_type='symbol'
      //         edges(id, source_node_id, target_node_id, edge_type_id, metadata)
      //         edge_types(id, name), files(id, path)
      if (req.method === 'GET' && url.pathname === '/api/projects/symbols') {
        const projectRoot = url.searchParams.get('project');
        const query = url.searchParams.get('q') ?? '';
        const kind = url.searchParams.get('kind') ?? '';
        const limit = Math.min(parseInt(url.searchParams.get('limit') ?? '50', 10), 200);
        const isolated = url.searchParams.get('isolated') === 'true';
        if (!projectRoot) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'Missing ?project= query param' }));
          return;
        }
        const resolution = resolveProjectForRest(projectRoot);
        if (!resolution.ok) {
          writeProjectResolutionError(res, resolution);
          return;
        }
        const managed = resolution.managed;
        try {
          const db = managed.store.db;
          const { sql, params } = buildSymbolsSearchQuery(query, kind, limit, isolated);

          const symbols = db.prepare(sql).all(...params);
          res.writeHead(200, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ symbols, count: symbols.length }));
        } catch (e) {
          res.writeHead(500, { 'Content-Type': 'application/json' });
          res.end(
            JSON.stringify({ error: (e as Error & { stack?: string })?.message ?? 'Query failed' }),
          );
        }
        return;
      }

      // REST API: get edges for a specific symbol (debug view)
      if (req.method === 'GET' && url.pathname === '/api/projects/symbol/edges') {
        const projectRoot = url.searchParams.get('project');
        const symbolId = url.searchParams.get('id');
        if (!projectRoot || !symbolId) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'Missing ?project= or ?id= query param' }));
          return;
        }
        const resolution = resolveProjectForRest(projectRoot);
        if (!resolution.ok) {
          writeProjectResolutionError(res, resolution);
          return;
        }
        const managed = resolution.managed;
        try {
          const db = managed.store.db;
          const symbol = db
            .prepare(`
            SELECT s.id, s.fqn, s.kind, f.path as file_path, s.line_start, s.line_end
            FROM symbols s JOIN files f ON f.id = s.file_id WHERE s.id = ?
          `)
            .get(symbolId);
          // Find the node for this symbol
          const node = db
            .prepare(`SELECT id FROM nodes WHERE node_type = 'symbol' AND ref_id = ?`)
            .get(symbolId) as Record<string, unknown> | undefined;
          let outgoing: unknown[] = [];
          let incoming: unknown[] = [];
          if (node) {
            outgoing = db
              .prepare(`
              SELECT et.name as type, e.metadata,
                     ts.id, ts.fqn, ts.kind, tf.path as file_path
              FROM edges e
              JOIN edge_types et ON et.id = e.edge_type_id
              JOIN nodes tn ON tn.id = e.target_node_id
              LEFT JOIN symbols ts ON tn.node_type = 'symbol' AND tn.ref_id = ts.id
              LEFT JOIN files tf ON tf.id = ts.file_id
              WHERE e.source_node_id = ?
            `)
              .all(node.id);
            incoming = db
              .prepare(`
              SELECT et.name as type, e.metadata,
                     ss.id, ss.fqn, ss.kind, sf.path as file_path
              FROM edges e
              JOIN edge_types et ON et.id = e.edge_type_id
              JOIN nodes sn ON sn.id = e.source_node_id
              LEFT JOIN symbols ss ON sn.node_type = 'symbol' AND sn.ref_id = ss.id
              LEFT JOIN files sf ON sf.id = ss.file_id
              WHERE e.target_node_id = ?
            `)
              .all(node.id);
          }
          res.writeHead(200, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ symbol, nodeId: node?.id ?? null, outgoing, incoming }));
        } catch (e) {
          res.writeHead(500, { 'Content-Type': 'application/json' });
          res.end(
            JSON.stringify({ error: (e as Error & { stack?: string })?.message ?? 'Query failed' }),
          );
        }
        return;
      }

      // REST API: graph data (nodes + edges for D3 visualization)
      if (req.method === 'GET' && url.pathname === '/api/projects/graph') {
        const projectRoot = url.searchParams.get('project');
        if (!projectRoot) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'Missing ?project= query param' }));
          return;
        }
        const resolution = resolveProjectForRest(projectRoot);
        if (!resolution.ok) {
          writeProjectResolutionError(res, resolution);
          return;
        }
        const managed = resolution.managed;
        try {
          const scope = url.searchParams.get('scope') ?? 'project';
          const depth = parseInt(url.searchParams.get('depth') ?? '2', 10);
          const granularity = (url.searchParams.get('granularity') ?? 'file') as 'file' | 'symbol';
          const layout = (url.searchParams.get('layout') ?? 'force') as
            | 'force'
            | 'hierarchical'
            | 'radial';
          const hideIsolated = url.searchParams.get('hideIsolated') !== 'false';
          const symbolKinds = url.searchParams.get('symbolKinds')?.split(',').filter(Boolean);
          const maxFiles = url.searchParams.has('maxFiles')
            ? parseInt(url.searchParams.get('maxFiles')!, 10)
            : undefined;
          const maxNodes = url.searchParams.has('maxNodes')
            ? parseInt(url.searchParams.get('maxNodes')!, 10)
            : undefined;
          const includeBottlenecks = url.searchParams.get('includeBottlenecks') === 'true';

          // Open topoStore for subproject support (best-effort)
          let topoStore: InstanceType<typeof TopologyStore> | undefined;
          try {
            if (fs.existsSync(TOPOLOGY_DB_PATH)) topoStore = new TopologyStore(TOPOLOGY_DB_PATH);
          } catch {
            /* subproject support is optional */
          }

          try {
            const { nodes, edges, communities } = buildGraphData(managed.store, {
              scope,
              depth,
              granularity,
              layout,
              hideIsolated,
              symbolKinds,
              maxFiles,
              maxNodes,
              topoStore,
              projectRoot,
              includeBottlenecks,
            });

            res.writeHead(200, { 'Content-Type': 'application/json' });
            res.end(JSON.stringify({ nodes, edges, communities }));
          } finally {
            topoStore?.close();
          }
        } catch (e) {
          res.writeHead(500, { 'Content-Type': 'application/json' });
          res.end(
            JSON.stringify({
              error: (e as Error & { stack?: string })?.message ?? 'Graph build failed',
            }),
          );
        }
        return;
      }

      // REST API: graph as self-contained HTML (D3.js visualization)
      if (req.method === 'GET' && url.pathname === '/api/projects/graph/html') {
        const projectRoot = url.searchParams.get('project');
        if (!projectRoot) {
          res.writeHead(400, { 'Content-Type': 'text/plain' });
          res.end('Missing ?project= query param');
          return;
        }
        const resolution = resolveProjectForRest(projectRoot);
        if (!resolution.ok) {
          writeProjectResolutionError(res, resolution, 'text');
          return;
        }
        const managed = resolution.managed;
        try {
          const scope = url.searchParams.get('scope') ?? 'project';
          const depth = parseInt(url.searchParams.get('depth') ?? '2', 10);
          const granularity = (url.searchParams.get('granularity') ?? 'file') as 'file' | 'symbol';
          const layout = (url.searchParams.get('layout') ?? 'force') as
            | 'force'
            | 'hierarchical'
            | 'radial';
          const hideIsolated = url.searchParams.get('hideIsolated') !== 'false';
          const symbolKinds = url.searchParams.get('symbolKinds')?.split(',').filter(Boolean);
          const maxFiles = url.searchParams.has('maxFiles')
            ? parseInt(url.searchParams.get('maxFiles')!, 10)
            : undefined;
          const maxNodes = url.searchParams.has('maxNodes')
            ? parseInt(url.searchParams.get('maxNodes')!, 10)
            : undefined;
          const highlightDepth = url.searchParams.has('highlightDepth')
            ? parseInt(url.searchParams.get('highlightDepth')!, 10)
            : undefined;

          // Open topoStore for subproject support (best-effort)
          let topoStore: InstanceType<typeof TopologyStore> | undefined;
          try {
            if (fs.existsSync(TOPOLOGY_DB_PATH)) topoStore = new TopologyStore(TOPOLOGY_DB_PATH);
          } catch {
            /* subproject support is optional */
          }

          try {
            const { nodes, edges, communities } = buildGraphData(managed.store, {
              scope,
              depth,
              granularity,
              layout,
              hideIsolated,
              symbolKinds,
              maxFiles,
              maxNodes,
              topoStore,
              projectRoot,
              highlightDepth,
            });
            let html = generateHtml(nodes, edges, communities, layout, { highlightDepth });

            // Embedded mode: adapt styling for iframe in the menu bar app
            const embedded = url.searchParams.get('embedded') === 'true';
            const theme = url.searchParams.get('theme') ?? 'dark';
            if (embedded) {
              const bg = theme === 'light' ? '#f0f0f0' : '#1c1c1e';
              const textColor = theme === 'light' ? '#333' : '#ccc';
              const embeddedCSS = `<style>
                body { background: ${bg} !important; }
                #controls { display: none !important; }
                #stats { display: none !important; }
                .node text { fill: ${textColor} !important; }
              </style>`;
              html = html.replace('</head>', `${embeddedCSS}</head>`);
            }

            // Send stats via headers so the client can skip the separate JSON fetch
            res.writeHead(200, {
              'Content-Type': 'text/html',
              'X-Graph-Nodes': String(nodes.length),
              'X-Graph-Edges': String(edges.length),
              'X-Graph-Communities': String(communities.length),
            });
            res.end(html);
          } finally {
            topoStore?.close();
          }
        } catch (e) {
          res.writeHead(500, { 'Content-Type': 'text/plain' });
          res.end((e as Error & { stack?: string })?.message ?? 'Graph build failed');
        }
        return;
      }

      // REST API: graph summary stats (for graph explorer)
      if (req.method === 'GET' && url.pathname === '/api/projects/graph-stats') {
        const projectRoot = url.searchParams.get('project');
        if (!projectRoot) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'Missing ?project= query param' }));
          return;
        }
        const resolution = resolveProjectForRest(projectRoot);
        if (!resolution.ok) {
          writeProjectResolutionError(res, resolution);
          return;
        }
        const managed = resolution.managed;
        try {
          const db = managed.store.db;
          const totalSymbols =
            (db.prepare('SELECT COUNT(*) as c FROM symbols').get() as { c: number })?.c ?? 0;
          const totalEdges =
            (db.prepare('SELECT COUNT(*) as c FROM edges').get() as { c: number })?.c ?? 0;
          const isolatedCount =
            (
              db
                .prepare(`
            SELECT COUNT(*) as c FROM symbols s
            WHERE NOT EXISTS (
              SELECT 1 FROM nodes n
              JOIN edges e ON e.source_node_id = n.id OR e.target_node_id = n.id
              WHERE n.node_type = 'symbol' AND n.ref_id = s.id
            )
          `)
                .get() as { c: number } | undefined
            )?.c ?? 0;

          const kindBreakdown = db
            .prepare(
              'SELECT kind, COUNT(*) as count FROM symbols GROUP BY kind ORDER BY count DESC',
            )
            .all();
          const edgeBreakdown = db
            .prepare(`
            SELECT et.name as type, COUNT(*) as count
            FROM edges e JOIN edge_types et ON et.id = e.edge_type_id
            GROUP BY et.name ORDER BY count DESC
          `)
            .all();

          res.writeHead(200, { 'Content-Type': 'application/json' });
          res.end(
            JSON.stringify({
              totalSymbols,
              totalEdges,
              isolatedCount,
              kindBreakdown,
              edgeBreakdown,
            }),
          );
        } catch (e) {
          res.writeHead(500, { 'Content-Type': 'application/json' });
          res.end(
            JSON.stringify({ error: (e as Error & { stack?: string })?.message ?? 'Query failed' }),
          );
        }
        return;
      }

      // REST API: project stats (files, symbols, edges)
      if (req.method === 'GET' && url.pathname === '/api/projects/stats') {
        const projectRoot = url.searchParams.get('project');
        if (!projectRoot) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'Missing ?project= query param' }));
          return;
        }
        const resolution = resolveProjectForRest(projectRoot);
        if (!resolution.ok) {
          writeProjectResolutionError(res, resolution);
          return;
        }
        const managed = resolution.managed;
        try {
          const db = managed.store.db;
          const files =
            (db.prepare('SELECT COUNT(*) as c FROM files').get() as { c: number })?.c ?? 0;
          const symbols =
            (db.prepare('SELECT COUNT(*) as c FROM symbols').get() as { c: number })?.c ?? 0;
          const edges =
            (db.prepare('SELECT COUNT(*) as c FROM edges').get() as { c: number })?.c ?? 0;
          const lastRow = db.prepare('SELECT MAX(indexed_at) as t FROM files').get() as
            | { t: string | null }
            | undefined;
          // TRA-1664: the last full walk may have hit security.max_files, in
          // which case the index is partial and the UI must say so. The stamp
          // survives restarts (incremental fast paths never re-walk the tree).
          const truncated = readIndexTruncation(managed.store);
          res.writeHead(200, { 'Content-Type': 'application/json' });
          res.end(
            JSON.stringify({
              files,
              symbols,
              edges,
              lastIndexed: sqliteUtcToIso(lastRow?.t),
              ...(truncated ? { truncated } : {}),
            }),
          );
        } catch (e) {
          res.writeHead(500, { 'Content-Type': 'application/json' });
          res.end(
            JSON.stringify({
              error: (e as Error & { stack?: string })?.message ?? 'Failed to get stats',
            }),
          );
        }
        return;
      }

      // REST API: files a linked git worktree changed relative to the canonical
      // checkout's index (the checkout a worktree session is served from).
      // `?project=` is the worktree root; the canonical checkout must be a
      // registered project. A main checkout answers `is_linked_worktree: false`.
      if (req.method === 'GET' && url.pathname === '/api/projects/worktree') {
        const worktreeRoot = url.searchParams.get('project');
        if (!worktreeRoot) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'Missing ?project= query param' }));
          return;
        }
        const link = resolveWorktreeLink(worktreeRoot);
        if (!link) {
          res.writeHead(200, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ is_linked_worktree: false }));
          return;
        }
        if (!getProject(link.canonicalRoot)) {
          res.writeHead(404, { 'Content-Type': 'application/json' });
          res.end(
            JSON.stringify({
              error: `Canonical checkout "${path.basename(link.canonicalRoot)}" isn't registered with this daemon.`,
            }),
          );
          return;
        }
        const delta = await getWorktreeDelta(link);
        if (!delta) {
          res.writeHead(503, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'git could not compute the worktree delta' }));
          return;
        }
        res.writeHead(200, { 'Content-Type': 'application/json' });
        res.end(
          JSON.stringify(
            summarizeWorktreeDelta(delta, undefined, worktreeIndexManager.describe(link)),
          ),
        );
        return;
      }

      // REST API: drop a linked worktree's branch index (WorktreeRemove hook).
      // Deletes only copies whose sidecar names this worktree; the path itself
      // may already be gone. Never touches the canonical index.
      if (req.method === 'DELETE' && url.pathname === '/api/projects/worktree') {
        const worktreeRoot = url.searchParams.get('project');
        if (!worktreeRoot || !path.isAbsolute(worktreeRoot)) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'Missing or relative ?project= query param' }));
          return;
        }
        const dropped = await worktreeIndexManager.drop(worktreeRoot);
        res.writeHead(200, { 'Content-Type': 'application/json' });
        res.end(JSON.stringify({ dropped }));
        return;
      }

      // REST API: quality findings (code smells incl. debug artifacts)
      if (req.method === 'GET' && url.pathname === '/api/projects/smells') {
        const projectRoot = url.searchParams.get('project');
        if (!projectRoot) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'Missing ?project= query param' }));
          return;
        }
        const resolution = resolveProjectForRest(projectRoot);
        if (!resolution.ok) {
          writeProjectResolutionError(res, resolution);
          return;
        }
        const managed = resolution.managed;
        const categoryParam = url.searchParams.get('category');
        const categories = categoryParam
          ? categoryParam
              .split(',')
              .map((c) => c.trim())
              .filter(Boolean)
          : undefined;
        const priorityThreshold = url.searchParams.get('priority_threshold') ?? undefined;
        const limit = Math.min(parseInt(url.searchParams.get('limit') ?? '500', 10), 2000);
        try {
          type SmellOpts = NonNullable<Parameters<typeof scanCodeSmells>[2]>;
          const result = scanCodeSmells(managed.store, projectRoot, {
            category: categories as SmellOpts['category'],
            priority_threshold: priorityThreshold as SmellOpts['priority_threshold'],
            limit,
          });
          if (result.isErr()) {
            res.writeHead(500, { 'Content-Type': 'application/json' });
            res.end(JSON.stringify({ error: String(result.error) }));
            return;
          }
          res.writeHead(200, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify(result.value));
        } catch (e) {
          res.writeHead(500, { 'Content-Type': 'application/json' });
          res.end(
            JSON.stringify({
              error: (e as Error & { stack?: string })?.message ?? 'Failed to scan code smells',
            }),
          );
        }
        return;
      }

      // REST API: security findings (OWASP pattern scan) for a project.
      // Mirrors /api/projects/smells above: the Workspace table only carries
      // the critical+high count, and Overview needs the list itself
      // (severity/rule/file:line/snippet) with a severity filter (TRA-1675).
      if (req.method === 'GET' && url.pathname === '/api/projects/security') {
        const projectRoot = url.searchParams.get('project');
        if (!projectRoot) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'Missing ?project= query param' }));
          return;
        }
        const resolution = resolveProjectForRest(projectRoot);
        if (!resolution.ok) {
          writeProjectResolutionError(res, resolution);
          return;
        }
        const managed = resolution.managed;
        const parsed = parseProjectSecurityQuery(url);
        if (!parsed.ok) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: parsed.error }));
          return;
        }
        try {
          const result = scanSecurity(managed.store, projectRoot, {
            rules: parsed.value.rules,
            severityThreshold: parsed.value.severityThreshold,
            includeLowConfidence: parsed.value.includeLowConfidence,
          });
          if (result.isErr()) {
            res.writeHead(500, { 'Content-Type': 'application/json' });
            res.end(JSON.stringify({ error: String(result.error) }));
            return;
          }
          const value = result.value;
          const findings = value.findings.slice(0, parsed.value.limit);
          res.writeHead(200, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ ...value, findings }));
        } catch (e) {
          res.writeHead(500, { 'Content-Type': 'application/json' });
          res.end(
            JSON.stringify({
              error: (e as Error & { stack?: string })?.message ?? 'Failed to scan security',
            }),
          );
        }
        return;
      }

      // REST API: technology coverage analysis for a project
      // Recursively walks sub-projects so monorepo / multi-service containers
      // (whose root has no manifest) aggregate coverage from all children.
      if (req.method === 'GET' && url.pathname === '/api/projects/coverage') {
        const projectRoot = url.searchParams.get('project');
        if (!projectRoot) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'Missing ?project= query param' }));
          return;
        }
        try {
          const multi = detectCoverageRecursive(projectRoot);

          // Deduplicate gaps across sub-projects by package name, keeping highest priority.
          const gapPrio = { high: 0, medium: 1, low: 2 } as const;
          const gapMap = new Map<string, (typeof multi.projects)[number]['gaps'][number]>();
          for (const p of multi.projects) {
            for (const g of p.gaps) {
              const existing = gapMap.get(g.name);
              if (!existing || (gapPrio[g.priority] ?? 3) < (gapPrio[existing.priority] ?? 3)) {
                gapMap.set(g.name, g);
              }
            }
          }
          const gaps = [...gapMap.values()].sort(
            (a, b) => (gapPrio[a.priority] ?? 3) - (gapPrio[b.priority] ?? 3),
          );

          // Deduplicate unknown packages by name, keeping strongest signal (likely > maybe > no).
          const needPrio = { likely: 0, maybe: 1, no: 2 } as const;
          const unknownMap = new Map<string, (typeof multi.projects)[number]['unknown'][number]>();
          for (const p of multi.projects) {
            for (const u of p.unknown) {
              const existing = unknownMap.get(u.name);
              if (
                !existing ||
                (needPrio[u.needs_plugin] ?? 3) < (needPrio[existing.needs_plugin] ?? 3)
              ) {
                unknownMap.set(u.name, u);
              }
            }
          }
          const unknown = [...unknownMap.values()].sort(
            (a, b) => (needPrio[a.needs_plugin] ?? 3) - (needPrio[b.needs_plugin] ?? 3),
          );

          res.writeHead(200, { 'Content-Type': 'application/json' });
          res.end(
            JSON.stringify({
              coverage: {
                total_significant: multi.aggregate.total_significant,
                covered: multi.aggregate.covered,
                coverage_pct: multi.aggregate.coverage_pct,
              },
              gaps,
              unknown,
            }),
          );
        } catch (e) {
          res.writeHead(500, { 'Content-Type': 'application/json' });
          res.end(
            JSON.stringify({
              error: (e as Error & { stack?: string })?.message ?? 'Failed to detect coverage',
            }),
          );
        }
        return;
      }

      // REST API: list files with sort options (for app sidebar)
      if (req.method === 'GET' && url.pathname === '/api/projects/files') {
        const projectRoot = url.searchParams.get('project');
        const sortBy = url.searchParams.get('sort') ?? 'symbols';
        const limit = Math.min(parseInt(url.searchParams.get('limit') ?? '30', 10), 100);
        const scope = url.searchParams.get('scope')?.trim() || '';
        if (!projectRoot) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'Missing ?project= query param' }));
          return;
        }
        const resolution = resolveProjectForRest(projectRoot);
        if (!resolution.ok) {
          writeProjectResolutionError(res, resolution);
          return;
        }
        const managed = resolution.managed;
        try {
          const db = managed.store.db;
          const { sql, params } = buildProjectFilesQuery(sortBy, scope, limit);
          const files = db.prepare(sql).all(...params) as {
            path: string;
            symbols: number;
            edges: number;
          }[];
          res.writeHead(200, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ files, sort: sortBy }));
        } catch (e) {
          res.writeHead(500, { 'Content-Type': 'application/json' });
          res.end(
            JSON.stringify({ error: (e as Error & { stack?: string })?.message ?? 'Query failed' }),
          );
        }
        return;
      }

      // REST API: list subprojects for a project
      if (req.method === 'GET' && url.pathname === '/api/projects/subprojects') {
        try {
          let topoStore: InstanceType<typeof TopologyStore> | undefined;
          try {
            if (fs.existsSync(TOPOLOGY_DB_PATH)) topoStore = new TopologyStore(TOPOLOGY_DB_PATH);
          } catch {
            /* subproject support is optional */
          }

          if (!topoStore) {
            res.writeHead(200, { 'Content-Type': 'application/json' });
            res.end(JSON.stringify({ repos: [], services: [] }));
            return;
          }

          try {
            const manager = new SubprojectManager(topoStore);
            const projectRoot = url.searchParams.get('project') ?? undefined;
            const result = manager.list(projectRoot);

            // Also return services with project_group for UI grouping
            const servicesWithCounts = topoStore.getServicesWithEndpointCounts(projectRoot);
            const services = servicesWithCounts.map((s) => ({
              id: s.id,
              name: s.name,
              repoRoot: s.repo_root,
              serviceType: s.service_type,
              projectGroup: s.project_group,
              endpointCount: s.endpoint_count,
            }));

            res.writeHead(200, { 'Content-Type': 'application/json' });
            res.end(JSON.stringify({ repos: result.repos, services }));
          } finally {
            topoStore.close();
          }
        } catch (e) {
          res.writeHead(500, { 'Content-Type': 'application/json' });
          res.end(
            JSON.stringify({
              error: (e as Error & { stack?: string })?.message ?? 'Subproject query failed',
            }),
          );
        }
        return;
      }

      // REST API: update service project_group
      if (req.method === 'PATCH' && url.pathname === '/api/projects/services') {
        let body = '';
        req.on('data', (chunk: Buffer) => {
          body += chunk.toString();
        });
        req.on('end', () => {
          try {
            const { serviceId, projectGroup } = JSON.parse(body) as {
              serviceId: number;
              projectGroup: string | null;
            };
            if (serviceId == null) {
              res.writeHead(400, { 'Content-Type': 'application/json' });
              res.end(JSON.stringify({ error: 'serviceId is required' }));
              return;
            }

            let topoStore: InstanceType<typeof TopologyStore> | undefined;
            try {
              ensureGlobalDirs();
              topoStore = new TopologyStore(TOPOLOGY_DB_PATH);
            } catch (e) {
              res.writeHead(500, { 'Content-Type': 'application/json' });
              res.end(
                JSON.stringify({
                  error: (e as Error & { stack?: string })?.message ?? 'Failed to open topology DB',
                }),
              );
              return;
            }

            try {
              topoStore.updateServiceGroup(serviceId, projectGroup ?? null);
              res.writeHead(200, { 'Content-Type': 'application/json' });
              res.end(JSON.stringify({ ok: true }));
            } finally {
              topoStore.close();
            }
          } catch (_e) {
            res.writeHead(400, { 'Content-Type': 'application/json' });
            res.end(JSON.stringify({ error: 'Invalid JSON body' }));
          }
        });
        return;
      }

      // REST API: add a subproject to a project
      if (req.method === 'POST' && url.pathname === '/api/projects/subprojects') {
        let body = '';
        req.on('data', (chunk: Buffer) => {
          body += chunk.toString();
        });
        req.on('end', async () => {
          try {
            const { repoPath, project } = JSON.parse(body) as { repoPath: string; project: string };
            if (!repoPath || !project) {
              res.writeHead(400, { 'Content-Type': 'application/json' });
              res.end(JSON.stringify({ error: 'repoPath and project are required' }));
              return;
            }

            let topoStore: InstanceType<typeof TopologyStore> | undefined;
            try {
              ensureGlobalDirs();
              topoStore = new TopologyStore(TOPOLOGY_DB_PATH);
            } catch (e) {
              res.writeHead(500, { 'Content-Type': 'application/json' });
              res.end(
                JSON.stringify({
                  error: (e as Error & { stack?: string })?.message ?? 'Failed to open topology DB',
                }),
              );
              return;
            }

            try {
              const manager = new SubprojectManager(topoStore);
              const result = await manager.add(repoPath, project);
              res.writeHead(200, { 'Content-Type': 'application/json' });
              res.end(JSON.stringify(result));
            } catch (e) {
              res.writeHead(400, { 'Content-Type': 'application/json' });
              res.end(
                JSON.stringify({
                  error: (e as Error & { stack?: string })?.message ?? 'Failed to add subproject',
                }),
              );
            } finally {
              topoStore?.close();
            }
          } catch (_e) {
            res.writeHead(400, { 'Content-Type': 'application/json' });
            res.end(JSON.stringify({ error: 'Invalid JSON body' }));
          }
        });
        return;
      }

      // REST API: remove a subproject
      if (req.method === 'DELETE' && url.pathname === '/api/projects/subprojects') {
        const name = url.searchParams.get('name');
        if (!name) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'name parameter is required' }));
          return;
        }

        try {
          let topoStore: InstanceType<typeof TopologyStore> | undefined;
          try {
            if (fs.existsSync(TOPOLOGY_DB_PATH)) topoStore = new TopologyStore(TOPOLOGY_DB_PATH);
          } catch {
            /* subproject support is optional */
          }

          if (!topoStore) {
            res.writeHead(404, { 'Content-Type': 'application/json' });
            res.end(JSON.stringify({ error: 'No subproject data' }));
            return;
          }

          try {
            const manager = new SubprojectManager(topoStore);
            const removed = manager.remove(name);
            if (removed) {
              res.writeHead(200, { 'Content-Type': 'application/json' });
              res.end(JSON.stringify({ ok: true }));
            } else {
              res.writeHead(404, { 'Content-Type': 'application/json' });
              res.end(JSON.stringify({ error: `Subproject '${name}' not found` }));
            }
          } finally {
            topoStore.close();
          }
        } catch (e) {
          res.writeHead(500, { 'Content-Type': 'application/json' });
          res.end(
            JSON.stringify({
              error: (e as Error & { stack?: string })?.message ?? 'Subproject delete failed',
            }),
          );
        }
        return;
      }

      // REST API: list projects
      // TRA-1996: this endpoint reports *index-readiness* (last-known `ready`
      // for idle-unloaded roots, so the app's CTA reads "Reindex" — TRA-1052),
      // while `GET /health` reports *residency* (same root shows `unloaded`
      // there). The `status` values intentionally differ; both surfaces now
      // carry `resident` so callers correlate via that flag, not `status`.
      if (req.method === 'GET' && url.pathname === '/api/projects') {
        // Resident (in-memory) projects are a strict subset of what's
        // registered on disk — idle-unloaded projects and ones a daemon
        // restart hasn't reloaded yet stay registered but aren't resident.
        // Reporting only the resident set here made the app's CTA read the
        // *daemon's memory pressure* instead of the project's actual state:
        // an already-indexed, merely-unloaded project showed "+ Index
        // project" instead of "Reindex" (TRA-1052). A registered entry was
        // indexed at least once to get registered, so 'ready' is the correct
        // last-known status until a resident reload (via
        // resolveProjectForRest, above) says otherwise over SSE.
        // `resident: false` marks those last-known entries (see
        // buildApiProjectsList) — pair with /health's `unloaded` for them.
        const projects = buildApiProjectsList(projectManager.listProjects(), listProjects());
        res.writeHead(200, { 'Content-Type': 'application/json' });
        res.end(JSON.stringify({ projects }));
        return;
      }

      // REST API: trigger reindex for a project
      if (req.method === 'POST' && url.pathname === '/api/projects/reindex') {
        const projectRoot = url.searchParams.get('project');
        if (!projectRoot) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'Missing ?project= query param' }));
          return;
        }
        // A registered-but-idle-unloaded project answers "Reindex" from the
        // app's CTA (its last known status is 'ready') — resolving it here
        // lazily reloads it instead of 404ing; the reload's own indexAll()
        // covers the user's intent even though it isn't a forced rebuild.
        const resolution = resolveProjectForRest(projectRoot);
        if (!resolution.ok) {
          writeProjectResolutionError(res, resolution);
          return;
        }
        const managed = resolution.managed;
        // TRA-1674: refuse to start a full reindex against a project that is
        // already tearing down (same 503-with-retry contract as the
        // single-file handleReindexFile gate from TRA-1553) — otherwise the
        // run below races stopProject()'s db.close() and dies with "The
        // database connection is not open" (field: 2026-09-18, "Reindex
        // failed" for a project the idle-unload sweep evicted mid-run).
        if (isProjectStopping(projectRoot)) {
          res.writeHead(503, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'project is stopping', retryAfterSec: 5 }));
          return;
        }
        // R09 v2: lifecycle events around the reindex call.
        // started is fire-and-forget; completed/errored fire on the
        // async settlement of the pipeline promise.
        const reindexStartedAt = Date.now();
        broadcastEvent({
          type: 'reindex_started',
          project: projectRoot,
          pipeline: 'index',
        });
        // TRA-1674: register the full indexAll run as in-flight reindex work
        // so stopProject()'s bounded drain (waitForReindexDrain) waits for it
        // instead of closing the DB underneath it. The single-file paths
        // (handleReindexFile, register_edit) already register via
        // beginReindex; this endpoint was the one full-reindex path that did
        // not, which is exactly the race the field log showed.
        const endReindex = beginReindex(projectRoot);
        managed.pipeline
          .indexAll(true)
          .then((result) => {
            broadcastEvent({
              type: 'reindex_completed',
              project: projectRoot,
              pipeline: 'index',
              duration_ms: Date.now() - reindexStartedAt,
              summary: result as unknown as Record<string, unknown>,
            });
          })
          .catch((err) => {
            logger.error({ error: err, projectRoot }, 'Reindex failed');
            broadcastEvent({
              type: 'reindex_errored',
              project: projectRoot,
              pipeline: 'index',
              message: err instanceof Error ? err.message : String(err),
            });
          })
          .finally(endReindex);
        res.writeHead(202, { 'Content-Type': 'application/json' });
        res.end(JSON.stringify({ status: 'reindex_started', project: projectRoot }));
        return;
      }

      // REST API: incremental single-file reindex (called by the PostToolUse hook
      // and by `trace-mcp index-file` once the daemon is up — see plan-indexer-perf §1.1).
      // #1480: by default the request is validated, queued under the reindex
      // lock and answered 202 before the indexing runs — answering after it
      // made every edit on a large project outrun the hook's 2 s timeout.
      // `?wait=1` keeps the old contract (204 after the work, 503/500 on
      // failure) for callers that need the result, e.g. the bench scripts.
      if (req.method === 'POST' && url.pathname === '/api/projects/reindex-file') {
        const waitParam = url.searchParams.get('wait');
        const waitForResult = waitParam === '1' || waitParam === 'true';
        let parsed: { project?: string; path?: string };
        try {
          const body = await collectBody(req);
          parsed = JSON.parse(body.toString()) as { project?: string; path?: string };
        } catch (e) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: `invalid JSON body: ${(e as Error).message}` }));
          return;
        }
        // TRA-2032: normalize symlink aliases (`/tmp/x` vs `/private/tmp/x`
        // on macOS) to the registered spelling before touching anything else,
        // so the reindex lock, the in-flight mark, the recent-dedup key and
        // the idle clock all share one key with the MCP register_edit path.
        // `getProject` is alias-tolerant; `.root` is the canonical spelling.
        // Truly unregistered roots pass through unchanged and 404 below.
        const preManaged = parsed?.project ? projectManager.getProject(parsed.project) : undefined;
        // A linked worktree that is not a project of its own: the edit belongs
        // in that worktree's branch index, never in the canonical DB.
        if (!preManaged && typeof parsed?.project === 'string' && typeof parsed.path === 'string') {
          const routed = await worktreeIndexManager.reindexFile(parsed.project, parsed.path, {
            wait: ['1', 'true'].includes(url.searchParams.get('wait') ?? ''),
          });
          if (routed) {
            if (!routed.ok) {
              res.writeHead(routed.status, { 'Content-Type': 'application/json' });
              res.end(JSON.stringify({ error: routed.error }));
            } else if (routed.noCopy) {
              res.writeHead(202, { 'Content-Type': 'application/json' });
              res.end(
                JSON.stringify({ status: 'no_copy', reason: routed.noCopy, path: routed.relPath }),
              );
            } else {
              res.writeHead(204);
              res.end();
            }
            return;
          }
        }
        const effectiveProject = preManaged?.root ?? parsed?.project;
        // The project may be registered but idle-unloaded (project_idle_unload_minutes) —
        // getProject() alone would see it as gone and handleReindexFile would
        // 404 (non-retryable). Mirror the /mcp auto-register path: kick off a
        // lazy reload and answer 503 + Retry-After so the hook falls back and
        // retries transparently, same UX as a still-warming cold-start project.
        const idleUnloadedRoot =
          effectiveProject &&
          !projectManager.getProject(effectiveProject) &&
          getProject(effectiveProject)
            ? effectiveProject
            : undefined;
        if (idleUnloadedRoot) {
          void projectManager
            .addProject(idleUnloadedRoot)
            .then(() => {
              // Mirror the /mcp auto-register path: re-arm the progress
              // listener the idle-unload sweep tore down, so the reload's
              // indexing progress reaches SSE subscribers again.
              subscribeToProjectProgress(idleUnloadedRoot);
              broadcastEvent({
                type: 'project_status',
                project: idleUnloadedRoot,
                status: 'indexing',
              });
            })
            .catch((err) => {
              logger.warn(
                { err: String(err), projectRoot: idleUnloadedRoot },
                'Lazy reload of idle-unloaded project failed',
              );
            });
          res.writeHead(503, { 'Content-Type': 'application/json', 'Retry-After': '5' });
          res.end(JSON.stringify({ error: `project not ready: reloading` }));
          return;
        }
        pokeActivity(effectiveProject);
        const reindexRequest = { project: effectiveProject, path: parsed?.path };
        const reindexDeps = { getProject: (root: string) => projectManager.getProject(root) };
        const result = waitForResult
          ? await handleReindexFile(reindexRequest, reindexDeps)
          : acceptReindexFile(reindexRequest, reindexDeps);
        if (!result.ok) {
          // WHY Retry-After: the 503 branch fires when a project is still warming
          // on a cold daemon. Hook clients honour Retry-After and fall back to the
          // local CLI path transparently.
          const headers: Record<string, string> = { 'Content-Type': 'application/json' };
          if (result.status === 503) {
            headers['Retry-After'] = String(result.retryAfterSec);
          }
          res.writeHead(result.status, headers);
          res.end(JSON.stringify({ error: result.error }));
          return;
        }
        if (result.queued) {
          res.writeHead(202, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ status: 'queued', path: result.relPath }));
          return;
        }
        res.writeHead(204);
        res.end();
        return;
      }

      // GET /api/stats — daemon reindex telemetry summary (optional ?since=1h)
      // What trace-mcp gave back to this install (TRA-1091). Local read of
      // ~/.trace-mcp/savings.json; the desktop app renders exactly this.
      if (req.method === 'GET' && url.pathname === '/api/savings') {
        try {
          const { buildSavingsReport } = await import('./savings-report.js');
          res.writeHead(200, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify(buildSavingsReport()));
        } catch (err) {
          res.writeHead(500, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: String(err) }));
        }
        return;
      }

      // ── Benchmark Lab (TRA-1951) ──────────────────────────────────
      // In-app measured comparisons: the pinned recall-harness battery over
      // our own arms (file-reading control, minimal, standard). v1 runs
      // synchronously — the battery is eight fixtures, seconds of indexed
      // queries — and every run is persisted to ~/.trace/benchmark-runs.
      if (req.method === 'GET' && url.pathname === '/api/benchmark-lab/arms') {
        try {
          const { LAB_ARMS } = await import('./benchmark-lab/arms.js');
          res.writeHead(200, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ arms: LAB_ARMS }));
        } catch (err) {
          res.writeHead(500, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: String(err) }));
        }
        return;
      }

      if (req.method === 'GET' && url.pathname === '/api/benchmark-lab/fixtures') {
        try {
          const { loadLabFixtures } = await import('./benchmark-lab/runner.js');
          const fixtures = loadLabFixtures().map((f) => ({
            id: f.id,
            kind: f.kind,
            query: f.query,
            k: f.k ?? 10,
          }));
          res.writeHead(200, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ fixtures }));
        } catch (err) {
          res.writeHead(500, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: String(err) }));
        }
        return;
      }

      if (req.method === 'POST' && url.pathname === '/api/benchmark-lab/run') {
        try {
          const body = await collectBody(req);
          const parsed = (JSON.parse(body.toString() || '{}') ?? {}) as {
            project?: string;
            arms?: string[];
            fixtureIds?: string[];
          };
          const registered = listProjects();
          const projectRoot = parsed.project ?? registered[0]?.root;
          if (!projectRoot) {
            res.writeHead(400, { 'Content-Type': 'application/json' });
            res.end(
              JSON.stringify({
                error: 'No project selected and none registered. Add a project first.',
              }),
            );
            return;
          }
          const entry = getProject(projectRoot);
          if (!entry?.dbPath) {
            res.writeHead(400, { 'Content-Type': 'application/json' });
            res.end(
              JSON.stringify({
                error: `No index for ${projectRoot}. Run \`trace-mcp add ${projectRoot}\` first.`,
              }),
            );
            return;
          }
          const { runLab } = await import('./benchmark-lab/runner.js');
          const { isLabArmId } = await import('./benchmark-lab/arms.js');
          const arms = parsed.arms ?? undefined;
          if (arms && !arms.every(isLabArmId)) {
            res.writeHead(400, { 'Content-Type': 'application/json' });
            res.end(JSON.stringify({ error: 'Unknown arm. See GET /api/benchmark-lab/arms.' }));
            return;
          }
          const run = await runLab({
            projectRoot,
            arms: arms?.filter(isLabArmId),
            filterIds: parsed.fixtureIds ?? undefined,
          });
          const { saveLabRun } = await import('./benchmark-lab/store.js');
          const file = saveLabRun(run);
          res.writeHead(200, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ run, file }));
        } catch (err) {
          res.writeHead(500, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: String(err) }));
        }
        return;
      }

      if (req.method === 'GET' && url.pathname === '/api/benchmark-lab/runs') {
        try {
          const { getLabRun, listLabRuns } = await import('./benchmark-lab/store.js');
          const id = url.searchParams.get('id');
          if (id) {
            const run = getLabRun(id);
            if (!run) {
              res.writeHead(404, { 'Content-Type': 'application/json' });
              res.end(JSON.stringify({ error: `No Benchmark Lab run: ${id}` }));
              return;
            }
            res.writeHead(200, { 'Content-Type': 'application/json' });
            res.end(JSON.stringify({ run }));
            return;
          }
          res.writeHead(200, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ runs: listLabRuns() }));
        } catch (err) {
          res.writeHead(500, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: String(err) }));
        }
        return;
      }

      if (req.method === 'GET' && url.pathname === '/api/benchmark-lab/export') {
        try {
          const id = url.searchParams.get('id');
          if (!id) {
            res.writeHead(400, { 'Content-Type': 'application/json' });
            res.end(JSON.stringify({ error: 'Missing ?id= query param' }));
            return;
          }
          const { getLabRun } = await import('./benchmark-lab/store.js');
          const run = getLabRun(id);
          if (!run) {
            res.writeHead(404, { 'Content-Type': 'application/json' });
            res.end(JSON.stringify({ error: `No Benchmark Lab run: ${id}` }));
            return;
          }
          const { renderLabMarkdown } = await import('./benchmark-lab/markdown.js');
          res.writeHead(200, { 'Content-Type': 'text/markdown; charset=utf-8' });
          res.end(renderLabMarkdown(run));
        } catch (err) {
          res.writeHead(500, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: String(err) }));
        }
        return;
      }

      if (req.method === 'GET' && url.pathname === '/api/stats') {
        try {
          const { getReindexStats } = await import('./daemon/reindex-stats.js');
          const { parseDuration } = await import('./cli/daemon-stats.js');
          const { readSessionFallbacks, summarizeSessionFallbacks } = await import(
            './daemon/router/fallback-stats.js'
          );
          const sinceParam = url.searchParams.get('since');
          let sinceMs: number | undefined;
          if (sinceParam) {
            const parsed = parseDuration(sinceParam);
            if (parsed == null) {
              // Phase 5+7 audit fix: align with `daemon stats` CLI which exits
              // non-zero on bad duration. Silently falling back to all-time
              // hides a malformed query — return 400 instead.
              res.writeHead(400, { 'Content-Type': 'application/json' });
              res.end(
                JSON.stringify({
                  error: `invalid 'since' duration: ${sinceParam}. Use s/m/h/d suffix (e.g. 1h, 24h, 7d).`,
                }),
              );
              return;
            }
            sinceMs = parsed;
          }
          const summary = getReindexStats().summarize(sinceMs);
          res.writeHead(200, { 'Content-Type': 'application/json' });
          res.end(
            JSON.stringify({
              ...summary,
              session_fallbacks: summarizeSessionFallbacks(readSessionFallbacks(), {
                sinceMs,
              }),
            }),
          );
        } catch (err) {
          res.writeHead(500, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: String(err) }));
        }
        return;
      }

      // REST API: add a project
      if (req.method === 'POST' && url.pathname === '/api/projects') {
        try {
          const body = await collectBody(req);
          const { root } = JSON.parse(body.toString()) as { root: string };
          if (!root || !fs.existsSync(root)) {
            res.writeHead(400, { 'Content-Type': 'application/json' });
            res.end(JSON.stringify({ error: 'Invalid or missing root path' }));
            return;
          }
          const absRoot = path.resolve(root);
          const dangerReason = isDangerousProjectRoot(absRoot);
          if (dangerReason) {
            logger.warn(
              { root: absRoot, reason: dangerReason, ua: req.headers['user-agent'] },
              'Rejected dangerous project root — likely an MCP client spawned trace-mcp with cwd=/ or similar',
            );
            res.writeHead(400, { 'Content-Type': 'application/json' });
            res.end(
              JSON.stringify({
                error: `Refusing to register "${absRoot}" as a project: ${dangerReason}. Configure a "cwd" on the MCP server entry pointing at a specific source directory.`,
              }),
            );
            return;
          }

          const ancestor = resolveRegisteredAncestor(absRoot);
          if (ancestor && ancestor.root !== absRoot) {
            if (!projectManager.getProject(ancestor.root)) {
              await projectManager.addProject(ancestor.root);
              subscribeToProjectProgress(ancestor.root);
            }
            logger.info(
              { requested: absRoot, parent: ancestor.root },
              'Routing subdirectory request to registered parent project',
            );
            res.writeHead(200, { 'Content-Type': 'application/json' });
            res.end(
              JSON.stringify({
                status: 'using_parent',
                project: ancestor.root,
                requested: absRoot,
              }),
            );
            return;
          }

          // GH#1371 / TRA-1881: `auto_register.mode: "never"` (or `"ask"`)
          // refuses *new* implicit registrations with an honest 403 instead
          // of a cold index. Already-registered roots (and the ancestor path
          // above) still resolve — this only blocks creating fresh rows.
          if (!getProject(absRoot)) {
            const autoMode = getAutoRegisterMode();
            if (autoMode === 'never' || autoMode === 'ask') {
              logger.info(
                { root: absRoot, mode: autoMode },
                'Refused daemon project registration: auto_register.mode disables it',
              );
              res.writeHead(403, { 'Content-Type': 'application/json' });
              res.end(
                JSON.stringify({
                  error:
                    `Auto-registration is disabled (auto_register.mode: "${autoMode}"). ` +
                    `Register "${absRoot}" deliberately with \`trace-mcp add\` to index it.`,
                }),
              );
              return;
            }
          }

          await projectManager.addProject(absRoot);
          subscribeToProjectProgress(absRoot);
          broadcastEvent({ type: 'project_status', project: absRoot, status: 'indexing' });
          res.writeHead(201, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ status: 'added', project: absRoot }));
        } catch (e) {
          res.writeHead(500, { 'Content-Type': 'application/json' });
          res.end(
            JSON.stringify({
              error: (e as Error & { stack?: string })?.message ?? 'Failed to add project',
            }),
          );
        }
        return;
      }

      // REST API: remove a project
      if (req.method === 'DELETE' && url.pathname === '/api/projects') {
        const projectRoot = url.searchParams.get('project');
        if (!projectRoot) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'Missing ?project= query param' }));
          return;
        }
        const artifacts = await projectManager.removeProject(projectRoot, {
          keepDbFiles: false,
        });
        // Tear down progress listener, sessions, throttle keys, etc. for
        // this root BEFORE we emit the status event so listeners on the
        // SSE bus see a clean state.
        teardownProjectBookkeeping(projectRoot);
        broadcastEvent({ type: 'project_status', project: projectRoot, status: 'removed' });
        // Clean up any remaining client entries for this project (transport
        // onclose handlers typically already removed these via the teardown
        // above; this is a belt-and-braces sweep for stdio-registered clients
        // that aren't tied to an HTTP session).
        for (const [id, client] of clients) {
          if (client.project === projectRoot) {
            clients.delete(id);
            broadcastEvent({ type: 'client_disconnect', clientId: id, project: projectRoot });
          }
        }
        res.writeHead(200, { 'Content-Type': 'application/json' });
        res.end(
          JSON.stringify({
            status: 'removed',
            project: projectRoot,
            deletedFiles: artifacts.deleted.length,
            // TRA-1887: removeProjectArtifacts keeps the index DB while a
            // sibling entry or live holder still claims it — deletedFiles
            // alone can't tell "kept (shared)" from "nothing to delete".
            keptFiles: artifacts.kept.length,
            kept: artifacts.kept,
            freedBytes: artifacts.freedBytes,
            topology: artifacts.topology,
            decisions: artifacts.decisions,
            failures: artifacts.failures,
          }),
        );
        return;
      }

      // REST API: connected MCP clients
      if (req.method === 'GET' && url.pathname === '/api/clients') {
        res.writeHead(200, { 'Content-Type': 'application/json' });
        res.end(JSON.stringify({ clients: Array.from(clients.values()) }));
        return;
      }

      // Diagnostics: process memory + sizes of every known in-memory cache.
      // Symptom-driven leak debugging — keep response shape stable.
      if (req.method === 'GET' && url.pathname === '/debug/memory') {
        // TRA-2017: aggregate live session-journal pressure (the byte-capped
        // dedup store that dominates per-session heap on busy daemons).
        // TRA-2061: also keep the per-session rows — the totals above can't
        // tell "ten quiet sessions" from "one runaway session".
        let sessionJournalEntries = 0;
        let sessionJournalCompactBytes = 0;
        const sessionJournals: Array<{ sessionId: string; entries: number; compactBytes: number }> =
          [];
        for (const [sid, handle] of sessionHandles) {
          try {
            const entries = handle.journal.getTotalEntries();
            const compactBytes = handle.journal.getCompactBytes();
            sessionJournalEntries += entries;
            sessionJournalCompactBytes += compactBytes;
            sessionJournals.push({ sessionId: sid, entries, compactBytes });
          } catch {
            /* a closing session's journal must not break diagnostics */
          }
        }
        const wakeSizes = projectManager.getWakeBookkeepingSizes();
        const treeStats = getTreeCacheStats();
        const report = buildMemoryReport({
          clients,
          sseConnections,
          rateBuckets,
          lastProgressEmittedAt,
          progressUnsubscribers,
          projectSessions,
          sessionTransports,
          sessionHandles,
          sessionClients,
          sessionLastSeen,
          registeredProjects: projectManager.listProjects().length,
          resourcePoolEntries: resourcePool.getTrackedProjectCount(),
          sessionJournalEntries,
          sessionJournalCompactBytes,
          sessionJournals,
          loadedProjects: projectManager
            .listProjects()
            .map((p) => ({ root: p.root, status: p.status })),
          descendantWakeEntries: wakeSizes.descendantWakeEntries,
          stallWarnedRoots: wakeSizes.stallWarnedRoots,
          treeCacheEntries: treeStats.entries,
          treeCacheApproxBytes: treeStats.approxBytes,
        });
        res.writeHead(200, { 'Content-Type': 'application/json' });
        res.end(JSON.stringify(report));
        return;
      }

      // REST API: register a client (used by stdio serve processes to announce themselves)
      if (req.method === 'POST' && url.pathname === '/api/clients') {
        try {
          const body = await collectBody(req);
          const {
            id,
            project,
            transport: t,
            name,
          } = JSON.parse(body.toString()) as {
            id: string;
            project: string;
            transport: string;
            name?: string;
          };
          const now = new Date().toISOString();
          clients.set(id, {
            id,
            name,
            project,
            transport: t || 'stdio',
            connectedAt: now,
            lastSeen: now,
          });
          broadcastEvent({
            type: 'client_connect',
            clientId: id,
            transport: t || 'stdio',
            project,
            name,
          });
          pokeActivity(project);
          res.writeHead(201, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ status: 'registered' }));
        } catch (e) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(
            JSON.stringify({ error: (e as Error & { stack?: string })?.message ?? 'Bad request' }),
          );
        }
        return;
      }

      // REST API: update client info (e.g. name after MCP initialize)
      if (req.method === 'PATCH' && url.pathname === '/api/clients') {
        try {
          const body = await collectBody(req);
          const { id, name } = JSON.parse(body.toString()) as { id: string; name?: string };
          const existing = clients.get(id);
          if (existing) {
            if (name) existing.name = name;
            existing.lastSeen = new Date().toISOString();
            broadcastEvent({
              type: 'client_update',
              clientId: id,
              name,
              project: existing.project,
            });
          }
          res.writeHead(200, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ status: 'updated' }));
        } catch (e) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(
            JSON.stringify({ error: (e as Error & { stack?: string })?.message ?? 'Bad request' }),
          );
        }
        return;
      }

      // REST API: unregister a client
      if (req.method === 'DELETE' && url.pathname === '/api/clients') {
        const clientId = url.searchParams.get('id');
        if (clientId) {
          const existing = clients.get(clientId);
          clients.delete(clientId);
          broadcastEvent({ type: 'client_disconnect', clientId, project: existing?.project });
          pokeActivity(existing?.project);
        }
        res.writeHead(200, { 'Content-Type': 'application/json' });
        res.end(JSON.stringify({ status: 'removed' }));
        return;
      }

      // REST API: read global settings
      if (req.method === 'GET' && url.pathname === '/api/settings') {
        const raw = loadGlobalConfigRaw();
        // TRA-1054: same "folder gone → dead row" rule as the project
        // registry, applied to per-project config overrides — a second store
        // that drifts the same way and has no cleanup path of its own.
        const overrideProjects = (raw as { projects?: Record<string, unknown> }).projects ?? {};
        const staleProjectPaths = Object.keys(overrideProjects).filter((p) => !fs.existsSync(p));
        res.writeHead(200, { 'Content-Type': 'application/json' });
        res.end(
          JSON.stringify({
            settings: raw,
            path: GLOBAL_CONFIG_PATH,
            staleProjectPaths,
            daemon: {
              port,
              host,
              log_path: DAEMON_LOG_PATH,
              uptime: Math.floor((Date.now() - startedAt) / 1000),
              pid: process.pid,
            },
          }),
        );
        return;
      }

      // REST API: update global settings
      if (req.method === 'PUT' && url.pathname === '/api/settings') {
        try {
          const body = await collectBody(req);
          const incoming = JSON.parse(body.toString()) as Record<string, unknown>;

          // Validate known section types
          const errors = validateConfigUpdate(incoming);
          if (errors.length > 0) {
            res.writeHead(422, { 'Content-Type': 'application/json' });
            res.end(JSON.stringify({ error: 'Validation failed', details: errors }));
            return;
          }

          // Deep-merge onto the existing JSONC config so sibling nested keys
          // survive and comments/formatting aren't destroyed (#221).
          const merged = saveGlobalSettingsJsonc(incoming);
          res.writeHead(200, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ status: 'updated', settings: merged }));
        } catch (e) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(
            JSON.stringify({
              error: (e as Error & { stack?: string })?.message ?? 'Invalid JSON body',
            }),
          );
        }
        return;
      }

      // REST API: AI activity — recent AI requests with timing/status
      if (req.method === 'GET' && url.pathname === '/api/ai/activity') {
        const limit = Math.min(
          Math.max(parseInt(url.searchParams.get('limit') ?? '50', 10) || 50, 1),
          200,
        );
        res.writeHead(200, { 'Content-Type': 'application/json' });
        res.end(
          JSON.stringify({
            entries: aiTracker.getRecent(limit),
            stats: aiTracker.getStats(),
          }),
        );
        return;
      }

      // REST API: Ask — resolve LLM provider for a project
      if (req.method === 'GET' && url.pathname === '/api/ask/provider') {
        const projectRoot = url.searchParams.get('project');
        if (!projectRoot) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'Missing ?project= parameter' }));
          return;
        }
        const resolution = resolveProjectForRest(projectRoot, { requireReady: true });
        if (!resolution.ok) {
          writeProjectResolutionError(res, resolution);
          return;
        }
        const managed = resolution.managed;
        try {
          const { resolveProvider } = await import('./ai/ask-shared.js');
          // Reload config from disk so we pick up settings changed via the UI
          const freshConfig = await loadConfig(projectRoot);
          const config = freshConfig.isOk() ? freshConfig.value : managed.config;
          const provider = resolveProvider({}, config);
          res.writeHead(200, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ provider: provider.name }));
        } catch (e) {
          res.writeHead(200, { 'Content-Type': 'application/json' });
          res.end(
            JSON.stringify({
              error: (e as Error & { stack?: string })?.message ?? 'No provider available',
            }),
          );
        }
        return;
      }

      // REST API: Ask — stream LLM response with code context
      if (req.method === 'POST' && url.pathname === '/api/ask') {
        const projectRoot = url.searchParams.get('project');
        if (!projectRoot) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'Missing ?project= parameter' }));
          return;
        }
        const resolution = resolveProjectForRest(projectRoot, { requireReady: true });
        if (!resolution.ok) {
          writeProjectResolutionError(res, resolution);
          return;
        }
        const managed = resolution.managed;

        let body: {
          messages?: { role: string; content: string }[];
          model?: string;
          provider?: string;
          budget?: number;
        };
        try {
          const raw = await collectBody(req);
          body = JSON.parse(raw.toString());
        } catch {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'Invalid JSON body' }));
          return;
        }

        const messages = body.messages;
        if (!messages || !Array.isArray(messages) || messages.length === 0) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'messages array is required' }));
          return;
        }

        // Find the latest user message for context retrieval
        const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user');
        if (!lastUserMsg) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'No user message found' }));
          return;
        }

        // Start SSE response
        res.writeHead(200, {
          'Content-Type': 'text/event-stream',
          'Cache-Control': 'no-cache',
          Connection: 'keep-alive',
        });

        const sendEvent = (data: Record<string, unknown>) => {
          res.write(`data: ${JSON.stringify(data)}\n\n`);
        };

        try {
          const { resolveProvider, gatherContext, buildSystemPrompt, stripContextFromMessage } =
            await import('./ai/ask-shared.js');
          const freshConfig = await loadConfig(projectRoot);
          const config = freshConfig.isOk() ? freshConfig.value : managed.config;
          const provider = resolveProvider({ model: body.model, provider: body.provider }, config);

          // Phase 1: Retrieve context
          sendEvent({ type: 'phase', phase: 'retrieving' });
          const budget = body.budget ?? 12000;
          const context = await gatherContext(
            projectRoot,
            managed.store,
            managed.registry,
            lastUserMsg.content,
            budget,
          );

          // Phase 2: Build message array for LLM
          sendEvent({ type: 'phase', phase: 'streaming' });
          const systemMsg = { role: 'system' as const, content: buildSystemPrompt(projectRoot) };
          const chatMessages = [
            systemMsg,
            // Strip context from older user messages
            ...messages
              .slice(0, -1)
              .map((m) =>
                stripContextFromMessage(m as Parameters<typeof stripContextFromMessage>[0]),
              ),
            // Latest user message with fresh context
            {
              role: 'user' as const,
              content: `## Code Context\n\n${context}\n\n## Question\n\n${lastUserMsg.content}`,
            },
          ];

          // Keep history manageable
          while (chatMessages.length > 21) {
            chatMessages.splice(1, 2);
          }

          // Phase 3: Stream LLM response
          for await (const chunk of provider.streamChat(chatMessages, { maxTokens: 4096 })) {
            sendEvent({ type: 'chunk', content: chunk });
          }

          sendEvent({ type: 'done' });
        } catch (e) {
          sendEvent({
            type: 'error',
            message: (e as Error & { stack?: string })?.message ?? 'Unknown error',
          });
        }

        res.end();
        return;
      }

      // REST API: SSE event stream
      if (req.method === 'GET' && url.pathname === '/api/events') {
        res.writeHead(200, {
          'Content-Type': 'text/event-stream',
          'Cache-Control': 'no-cache',
          Connection: 'keep-alive',
        });
        // Send initial snapshot of all project statuses
        for (const p of projectManager.listProjects()) {
          const snap = p.progress.snapshot();
          res.write(
            `data: ${JSON.stringify({
              type: 'project_status',
              project: p.root,
              status: p.status,
              error: p.error,
              progress: snap,
            })}\n\n`,
          );
        }
        sseConnections.add(res);
        req.on('close', () => {
          sseConnections.delete(res);
        });
        // Keep-alive ping every 30s
        const keepAlive = setInterval(() => {
          try {
            res.write(': ping\n\n');
          } catch {
            clearInterval(keepAlive);
            sseConnections.delete(res);
          }
        }, 30_000);
        req.on('close', () => clearInterval(keepAlive));
        return;
      }

      // ── Activity tab — snapshot of recent journal entries for a project ──
      // Live updates flow through /api/events as `journal_entry` events; this
      // endpoint returns the last N recorded entries for the project — every
      // session of it, past ones included — so the tab can populate on first
      // mount.
      if (req.method === 'GET' && url.pathname === '/api/projects/journal') {
        const projectRoot = url.searchParams.get('project');
        const limit = Math.min(
          1000,
          Math.max(1, parseInt(url.searchParams.get('limit') ?? '200', 10) || 200),
        );
        if (!projectRoot) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'project query param is required' }));
          return;
        }
        // Read the durable log: it covers every session of this project, not
        // just the newest one, and it survives the disconnects and daemon
        // restarts that used to empty this feed (TRA-1071). Without a store
        // (open failed) fall back to the live journals so the tab still shows
        // the current session.
        let snapshot: ReturnType<typeof buildJournalSnapshot> = [];
        if (activityStore) {
          const now = Date.now();
          snapshot = activityStore
            .listForProject(projectRoot, now - 24 * 60 * 60 * 1000, now)
            .slice(-limit)
            .reverse()
            .map((r) => ({
              type: 'journal_entry' as const,
              project: projectRoot,
              ts: r.ts,
              tool: r.tool,
              params_summary: r.params_summary,
              result_count: r.result_count,
              result_tokens: r.result_tokens ?? undefined,
              latency_ms: r.latency_ms ?? undefined,
              is_error: r.is_error === 1,
              session_id: r.session_id,
            }));
        } else {
          const sids = projectSessions.get(projectRoot);
          for (const sid of sids ?? []) {
            const handle = sessionHandles.get(sid);
            if (handle)
              snapshot.push(...buildJournalSnapshot(handle.journal, projectRoot, sid, limit));
          }
          snapshot.sort((a, b) => b.ts - a.ts);
          snapshot = snapshot.slice(0, limit);
        }
        res.writeHead(200, { 'Content-Type': 'application/json' });
        res.end(JSON.stringify(snapshot));
        return;
      }

      // ── Activity stats — aggregated journal metrics for the Activity tab ─
      const journalStatsCtx: JournalStatsContext = {
        listEntriesForProject(projectRoot, since, until) {
          const end = until ?? Date.now();
          const start = since ?? end - 24 * 60 * 60 * 1000;
          if (activityStore) {
            return activityStore.listForProject(projectRoot, start, end).map((r) => ({
              ts: r.ts,
              tool: r.tool,
              params_summary: r.params_summary,
              result_count: r.result_count,
              result_tokens: r.result_tokens ?? undefined,
              latency_ms: r.latency_ms ?? undefined,
              is_error: r.is_error === 1,
              session_id: r.session_id,
            }));
          }
          const sids = projectSessions.get(projectRoot);
          if (!sids || sids.size === 0) return [];
          const out: ReturnType<typeof buildJournalSnapshot> = [];
          for (const sid of sids) {
            const handle = sessionHandles.get(sid);
            if (handle) out.push(...buildJournalSnapshot(handle.journal, projectRoot, sid, 10_000));
          }
          return out.filter((e) => e.ts >= start && e.ts <= end);
        },
        recordingSince: () => activityStore?.recordingSince(),
      };
      if (handleJournalStatsRequest(req, res, url, journalStatsCtx)) return;

      // ── Per-project deep-dive stats (Stats modal) ─────────────────────
      if (handleProjectStatsRequest(req, res, url, { journalStats: journalStatsCtx })) return;

      // ── Memory explorer (decisions / corpora / sessions) ──────────────
      if (handleMemoryRequest(req, res, url)) return;

      // ── Dashboard — aggregate health across all registered projects ───
      if (await handleDashboardRequest(req, res)) return;

      // ── Ask v2 — persistent chat sessions with context transparency ───
      if (await handleAskSessionsRequest(req, res, { projectManager, loadConfig })) return;

      res.writeHead(404);
      res.end();
    });

    let daemonShuttingDown = false;
    /** Set once we own the port; cleared on shutdown so it cannot re-register a dying PID. */
    let pidReassert: NodeJS.Timeout | undefined;
    // TRA-1828: event-loop lag observer. Bulk reindex passes starved the
    // daemon's only thread while daemon.log stayed green — clients saw
    // `daemon-disappeared` but the daemon logged nothing. The monitor counts
    // stalls and warn-logs each one so QA can tell "daemon busy" apart from
    // "daemon dead". Started in the listen() callback below, stopped here.
    let lagMonitor: EventLoopLagMonitor | null = null;
    let stallWatchdog: StallWatchdog | null = null;
    // TRA-2056: heap-pressure guard timer — stopped here so shutdown never
    // re-triggers a restart mid-drain.
    let stopHeapGuard: (() => void) | null = null;
    const shutdown = async (reason?: string) => {
      // Re-entrancy guard: a SIGTERM delivered while the event loop was starved
      // by an indexing run only gets *processed* at the next yield, and a second
      // signal (or an idle-exit racing the signal) could re-enter this. Run the
      // cleanup once.
      if (daemonShuttingDown) return;
      daemonShuttingDown = true;
      // TRA-1911: record the reason synchronously, before the first await. The
      // `exit` breadcrumb fires later with only a numeric code — this is what
      // attributes it. Must precede everything async: an awaited hang followed
      // by the bounded-exit forcing `process.exit()` would otherwise leave a
      // bare `exitCode` with no reason.
      noteDaemonShutdownReason(reason ?? 'unknown');
      lagMonitor?.stop();
      lagMonitor = null;
      // TRA-1957: release the stall-watchdog worker. Awaited best-effort —
      // terminate() resolves once the worker is gone; a wedged main thread
      // can't get here anyway, so this only runs on orderly shutdowns.
      await stallWatchdog?.stop().catch(() => {});
      stallWatchdog = null;
      // #237 point 3 / #236 defect 2 (daemon path): graceful shutdown awaits
      // async cleanup and only exits from httpServer.close()'s callback. If a
      // close hangs or the event loop is starved, the daemon would never die on
      // a legitimate SIGTERM. Arm a bounded hard-exit so termination is
      // guaranteed within a bounded time. Unref'd — never keeps the process up.
      //
      // Armed before anything else runs, including the diagnostic below: the
      // deadline is meant to be measured from the signal, and `launchctl print`
      // can burn its own 2s timeout first (TRA-850). A JS timer still cannot
      // interrupt a synchronous call in progress — this bounds when the clock
      // starts, not that call.
      //
      // TRA-849: exit 0, not 1. Everything reaching `shutdown()` is a requested
      // stop (SIGTERM/SIGINT/idle-exit); overrunning the deadline says our
      // cleanup was slow, not that the daemon died. Exit 1 made launchd log a
      // failed exit, `daemon status` report `last exit: 1`, and — because the
      // forced exit skips `recordDaemonCleanStop()` below — the clean-stop
      // telemetry count 56% of ordinary stops as unclean deaths. So record the
      // clean stop and drop the PID file here too: both are what the graceful
      // path would have done, and both are cheap and synchronous.
      const shutdownStartedAt = Date.now();
      armBoundedExit(reason ?? 'unknown', {
        deadlineMs: DAEMON_SHUTDOWN_DEADLINE_MS,
        exitCode: 0,
        onTimeout: () => {
          logger.warn(
            {
              reason: reason ?? 'unknown',
              deadlineMs: DAEMON_SHUTDOWN_DEADLINE_MS,
              elapsedMs: Date.now() - shutdownStartedAt,
              // TRA-1017, cause B: which sockets held httpServer.close() open.
              // `openSockets` is tracked via the 'connection' listener below;
              // nonzero here after SSE + transports were ended means an idle
              // keep-alive (or another stream) the shutdown never closed.
              openSockets: openSockets.size,
            },
            'Graceful shutdown exceeded its deadline — forcing exit',
          );
          clearInterval(pidReassert);
          clearOwnDaemonPidFile();
          recordDaemonCleanStop();
        },
      });
      // Field debugging of the 60s restart loop (desktop-app /health watchdog
      // firing `daemon restart` against a warming daemon) was blind because
      // shutdowns left no trace of WHO asked. Always say why we're going down.
      // `process.on('SIGTERM', shutdown)` passes the signal name as arg 1;
      // programmatic callers pass their own reason string.
      // For a signalled stop the reason alone says nothing about the source, so
      // attach what the process can observe about it (TRA-850). Only for
      // signals: a programmatic reason already names its caller.
      const isSignal = reason === 'SIGTERM' || reason === 'SIGINT' || reason === 'SIGHUP';
      logger.info(
        {
          reason: reason ?? 'unknown',
          uptimeSec: Math.floor((Date.now() - startedAt) / 1000),
          // TRA-1752: how much work is in flight at the moment of the signal
          // — the next field log can tell a quiet shutdown from a loaded one.
          projectsLoaded: projectManager.listProjects().length,
          reindexInFlight: countReindexingProjects(),
          ...(isSignal ? describeStopContext() : {}),
        },
        'Daemon shutting down',
      );
      // TRA-1752: refuse new per-project work synchronously, before the first
      // await below. stopProject() marks each project as it gets to it, but
      // Promise.all visits them in order — an HTTP reindex-file or
      // register_edit landing in that window would start a pipeline run
      // against a DB that closes moments later ("database connection is not
      // open" from inside an async continuation). The mark makes those
      // callers take the 503-with-retry path instead; stopProject() re-marks
      // idempotently per project.
      for (const managed of projectManager.listProjects()) {
        markProjectStopping(managed.root);
      }
      // Close SSE connections
      for (const res of sseConnections) {
        try {
          res.end();
        } catch {
          /* ignore */
        }
      }
      sseConnections.clear();
      // Unsubscribe progress listeners
      for (const unsub of progressUnsubscribers.values()) {
        try {
          unsub();
        } catch {
          /* ignore */
        }
      }
      progressUnsubscribers.clear();
      lastProgressEmittedAt.clear();
      // Dispose all session handles (flush journals, close owned resources)
      for (const h of sessionHandles.values()) {
        h.dispose();
        h.server.close().catch(() => {});
      }
      sessionHandles.clear();
      sessionClients.clear();
      // Close all session transports
      for (const transport of sessionTransports.values()) {
        await transport.close().catch(() => {});
      }
      sessionTransports.clear();
      projectSessions.clear();
      projectRelay.dispose();
      idleMonitor.stop();
      // Drain scheduler stages while every DB is still open (stages read
      // per-project stores and the shared scheduler state).
      await memoryScheduler.stop();
      clearInterval(activityPoker);
      clearInterval(rateBucketCleanup);
      clearInterval(clientSweep);
      clearInterval(journalSilenceCheck);
      try {
        stopHeapGuard?.();
      } catch {
        /* best-effort */
      }
      stopHeapGuard = null;
      try {
        stopActivityWatchdog?.();
      } catch {
        /* best-effort */
      }
      // TRA-849: which phase eats the shutdown budget is not answerable from
      // the log today — we only know the total sometimes exceeded 5s. Time the
      // one phase that can block on real work (`stopProject` awaits each
      // project's in-flight initial index) so the next field log says whether
      // that await is the cost, instead of us guessing.
      const projectsStopStartedAt = Date.now();
      await worktreeIndexManager.shutdown();
      await projectManager.shutdown();
      // TRA-1017: `openSockets` here separates cause A (projects slow) from
      // cause B (sockets slow) in the next field log — when the deadline
      // fires after this line, the holder is below, not above.
      logger.info(
        { elapsedMs: Date.now() - projectsStopStartedAt, openSockets: openSockets.size },
        'Projects stopped',
      );
      // TRA-1752: these close SQLite handles, so they run only after the
      // per-project drain above. Closing them first (the old order) let
      // still-draining work — session teardown recording activity, scheduler
      // stages, pipeline tails — run against closed handles. activityStore
      // stays open through the transport closes above for the same reason.
      activityStore?.close();
      resourcePool.disposeAll();
      // Drop our PID registration last: while it exists, clients treat the
      // daemon as alive-but-busy and refuse to restart it (TRA-421). Stop the
      // re-assert first — httpServer.close() below waits on live SSE sessions,
      // so a tick landing after the unlink would recreate daemon.pid naming a
      // process that is already on its way out (TRA-556).
      clearInterval(pidReassert);
      clearOwnDaemonPidFile();
      // Reaching here at all is what makes this stop "clean" (TRA-671).
      recordDaemonCleanStop();
      // TRA-1017, cause B: `httpServer.close(cb)` waits for EVERY live socket,
      // including idle keep-alive connections no request is using — a client
      // holding one open (desktop app, MCP client with a pooled agent) wedged
      // the graceful path after 'Projects stopped' in 11 of 36 field overruns.
      // SSE streams were ended and session transports closed above, so what
      // remains should be idle: close those without touching in-flight
      // requests (this is deliberately NOT closeAllConnections(), which would
      // destroy requests still being served).
      try {
        if (typeof httpServer.closeIdleConnections === 'function') {
          httpServer.closeIdleConnections();
        }
      } catch {
        /* best-effort — close() below still runs */
      }
      httpServer.close(() => {
        logger.info({ elapsedMs: Date.now() - shutdownStartedAt }, 'Daemon shutdown complete');
        process.exit(0);
      });
    };
    process.on('SIGINT', shutdown);
    process.on('SIGTERM', shutdown);
    // TRA-1911: SIGHUP arrived with no handler, so a hangup killed the daemon
    // without a word — not even the `Daemon shutting down` line. `isSignal`
    // above already classified SIGHUP; it was just never registered.
    process.on('SIGHUP', shutdown);

    httpServer.on('error', (err: NodeJS.ErrnoException) => {
      if (err.code === 'EADDRINUSE') {
        logger.warn({ port }, 'Port in use — another daemon instance is already running, exiting');
        // TRA-1911: the warn above is pino-buffered and `process.exit()` can
        // discard it — the sync breadcrumb is what survives. A bind-race loser
        // must never look like a silent death.
        noteDaemonShutdownReason('eaddrinuse-bind-race');
        process.exit(2);
      }
      logger.error({ err: String(err) }, 'httpServer error');
      noteDaemonShutdownReason('http-server-error');
      process.exit(1);
    });

    // ── Idle-exit monitor ───────────────────────────────────────
    // When launched by launchd (KeepAlive=true), idle-exit would cause an
    // immediate respawn loop — disable in that case. Otherwise default 15 min.
    const managedByLaunchd = process.env.TRACE_MCP_MANAGED_BY === 'launchd';
    const defaultIdleMinutes = managedByLaunchd ? 0 : 15;
    const configuredIdleMinutes =
      typeof globalRaw.daemon_idle_exit_minutes === 'number'
        ? globalRaw.daemon_idle_exit_minutes
        : defaultIdleMinutes;
    const idleMonitor = new DaemonIdleMonitor({
      idleTimeoutMs: configuredIdleMinutes * 60_000,
      isBusy: () => clients.size > 0 || sessionTransports.size > 0 || sseConnections.size > 0,
      onIdle: async () => {
        await shutdown('idle-exit');
      },
    });

    // ── Per-project idle-unload sweep ───────────────────────────
    // Distinct from the whole-daemon idle-exit monitor above: this unloads
    // individual cold projects (in-memory DB/pipeline/watcher/server) while
    // the daemon keeps serving everything else, reclaiming per-connection
    // SQLite cache/mmap for projects nobody has touched recently. Stays
    // registered — reloads lazily on the next request (see the /mcp
    // auto-register path and /api/projects/reindex-file). 0 disables.
    // The same sweep also enforces `daemon_eager_load_projects` as a steady-state
    // LRU ceiling, not just a startup budget: lazy loads (auto-register,
    // reindex-file) used to walk past it unchecked — TRA-422 measured an
    // eager-8 daemon holding 11 projects within three minutes of boot, ~100 MB
    // resident each. See docs/daemon-memory.md for the attribution.
    const configuredIdleUnloadMinutes =
      typeof globalRaw.project_idle_unload_minutes === 'number'
        ? globalRaw.project_idle_unload_minutes
        : 30;
    const configuredMaxLoadedProjects =
      typeof globalRaw.daemon_eager_load_projects === 'number'
        ? globalRaw.daemon_eager_load_projects
        : 8;
    projectManager.startIdleUnloadSweep(configuredIdleUnloadMinutes * 60_000, {
      maxLoaded: configuredMaxLoadedProjects,
      evictDeleted: true,
      onUnloaded: (roots) => {
        for (const root of roots) {
          // Same cleanup as the DELETE endpoint: without it the progress
          // listener closure pins the unloaded project's ProgressState for
          // the daemon's lifetime. Sessions are already gone (the sweep
          // skips refCount > 0 projects) so this is mostly the listener +
          // throttle-key teardown.
          teardownProjectBookkeeping(root);
          broadcastEvent({ type: 'project_status', project: root, status: 'unloaded' });
        }
      },
    });
    // Vitals breadcrumb (TRA-267): an OS-level kill or native crash runs no JS
    // handler, so the safety net logs nothing and daemon.log's last line is an
    // ordinary indexing message. A periodic memory/project line makes the
    // trajectory before such a gap readable after the fact.
    startVitalsLog({
      getCounts: () => {
        const loaded = projectManager.listProjects();
        // TRA-1843: one warn per stall episode (latched inside) so a project
        // wedged in indexing names itself instead of sitting silent in the
        // vitals line at `sweep_busy` forever.
        projectManager.warnOnIndexingStalls();
        return {
          loaded: loaded.length,
          // TRA-1125: initial-load status alone under-reports. Incremental
          // reindex runs with the project still marked `ready`, so a busy
          // daemon logged itself idle — see countReindexingProjects().
          // TRA-1763: the pipeline now marks its own runs too (indexAll /
          // indexFiles / deferred reconciles), so a project in initial load
          // is in BOTH sets — exclude marked roots from the status term so
          // one busy project counts once, not twice.
          indexing:
            loaded.filter(
              (p) => (p.status === 'indexing' || p.status === 'starting') && !isReindexing(p.root),
            ).length + countReindexingProjects(),
          // TRA-1625: sweep skip reasons, so a daemon holding far more than
          // maxLoaded while unloading nothing is diagnosable from the log.
          // TRA-1738: pass the ceiling too — without it `evictable` counts
          // only TTL victims and hides LRU-ceiling victims the sweep would
          // actually evict on this tick.
          sweep: projectManager.sweepEligibility(
            configuredIdleUnloadMinutes * 60_000,
            configuredMaxLoadedProjects,
          ),
          // TRA-1665: dropped-event / reconcile tally, so a per-minute
          // full-walk storm on a churn-heavy root is visible in the vitals.
          reconcile: getDroppedEventStats(),
        };
      },
    });
    // Heap-pressure guard (TRA-2056): a V8 OOM aborts mid-request with no JS
    // handler, so warn early and restart gracefully (drain via shutdown(),
    // letting launchd respawn) instead of dying by SIGABRT at the ceiling.
    // Disabled with TRACE_MCP_HEAP_GUARD=0.
    if (process.env.TRACE_MCP_HEAP_GUARD !== '0') {
      stopHeapGuard = startHeapGuard({
        getContext: () => ({
          projectsLoaded: projectManager.listProjects().length,
          reindexInFlight: countReindexingProjects(),
        }),
        onRestart: () => {
          void shutdown('heap-pressure');
        },
      });
    }
    // Track activity: wrap the client/session/SSE mutation points in-place via
    // a ticker that re-evaluates on every /health check (cheap, idempotent).
    // Plus explicit hook below on key mutations.
    const activityPoker = setInterval(() => idleMonitor.onActivity(), 10_000);
    activityPoker.unref();
    // Arm once on startup — daemon starts idle until the first client connects.
    idleMonitor.onActivity();

    // ── Memory background scheduler ─────────────────────────────
    // Long-lived background loop that periodically advances each registered
    // project's memory pyramid (mine → cluster → memo). Off by default —
    // opt-in via `config.memory.background.enabled = true`. The scheduler
    // self-throttles via a global serial queue (concurrency=1) so it cannot
    // race LLM calls or SQLite writes against itself.
    // Load a TraceMcpConfig for the scheduler. We're inside `serve-http`
    // which has no project-rooted config — fall back to cwd; if that fails
    // (running outside a project), use the no-arg form to get fully-defaulted
    // settings. Per-project overrides flow through ManagedProject.config later.
    const schedulerConfigResult = await loadConfig(process.cwd()).catch(() => null);
    const schedulerConfigFallback =
      schedulerConfigResult && schedulerConfigResult.isOk()
        ? schedulerConfigResult.value
        : (await loadConfig().catch(() => null))?.unwrapOr(undefined as never);
    if (!schedulerConfigFallback) {
      logger.warn('Could not load any config for memory scheduler; skipping start');
    }
    const memoryScheduler = new MemoryScheduler({
      projectManager,
      config: schedulerConfigFallback ?? ({} as never),
    });
    memoryScheduler.start();
    /**
     * Single activity hook surface. Bumps the idle monitor AND the memory
     * scheduler. `projectRoot` is optional — when provided the scheduler
     * updates only that project's lastActivityAt; otherwise it updates
     * every known project (coarse, used by hooks that don't know the
     * project, e.g. the periodic poker).
     */
    const pokeActivity = (projectRoot?: string): void => {
      idleMonitor.onActivity();
      memoryScheduler.notifyActivity(projectRoot);
      // Resets the per-project idle-unload clock (project_idle_unload_minutes).
      // No-op for the coarse (no-projectRoot) callers and for projects not
      // currently loaded.
      if (projectRoot) projectManager.touchActivity(projectRoot);
    };

    // ── Self-staleness detection ─────────────────────────────────
    // A long-running daemon would otherwise stay on the version it booted with
    // until someone manually kills it. checkAndInstallUpdate only runs at
    // startup. To catch external upgrades (stdio session's npm install -g,
    // postinstall hook, or Electron tab), periodically compare our bundled
    // version with the installed package.json on disk. On mismatch we shut
    // down and let the supervisor (launchd KeepAlive / tray watchdog) respawn
    // with the fresh binary. Cheap (one fs read every 10 min, sync but tiny).
    if (PKG_VERSION !== '0.0.0-dev') {
      let pkgPath: string | null = null;
      try {
        pkgPath = fileURLToPath(new URL('../package.json', import.meta.url));
      } catch {
        /* unresolvable (e.g. bundled into a single file) — skip */
      }
      if (pkgPath) {
        const stalenessTimer = setInterval(() => {
          try {
            const pkg = JSON.parse(fs.readFileSync(pkgPath!, 'utf-8')) as { version?: string };
            if (pkg.version && pkg.version !== PKG_VERSION) {
              logger.info(
                { running: PKG_VERSION, installed: pkg.version },
                'Installed version changed — shutting down so supervisor respawns with fresh binary',
              );
              void shutdown('version-staleness');
            }
          } catch {
            /* transient read error — try again next tick */
          }
        }, 10 * 60_000);
        stalenessTimer.unref();
      }
    }

    // ── Registry auto-update watchdog (TRA-180) ──────────────────
    // checkAndInstallUpdate otherwise runs once, shortly after startup. A daemon kept
    // alive for weeks by launchd/tray or a long-lived stdio client that never
    // reconnects therefore never re-hits the npm registry, so it can silently
    // sit on a stale version indefinitely (unlike the local-staleness check
    // above, which only catches upgrades installed by some *other* process).
    // Poll on the same cadence as the configured check interval — the cache
    // inside checkAndInstallUpdate makes this a cheap no-op between real
    // registry checks.
    if (PKG_VERSION !== '0.0.0-dev' && globalRaw.auto_update !== false) {
      const intervalHours =
        typeof globalRaw.auto_update_check_interval_hours === 'number'
          ? globalRaw.auto_update_check_interval_hours
          : 12;
      const updateWatchdog = setInterval(() => {
        void checkAndInstallUpdate({ checkIntervalHours: intervalHours }).then((updated) => {
          if (updated) {
            logger.info(
              'Auto-update: new version installed — shutting down so supervisor respawns',
            );
            void shutdown('auto-update');
          }
        });
      }, intervalHours * 3_600_000);
      updateWatchdog.unref();
    }

    // TRA-1017, cause B: track live sockets for the shutdown instrumentation
    // above. Attached before listen(), so no connection can arrive untracked.
    httpServer.on('connection', (socket: Socket) => {
      openSockets.add(socket);
      socket.on('close', () => {
        openSockets.delete(socket);
      });
    });

    httpServer.listen(port, host, () => {
      // TRA-525: we own the port — only now are we provably "the daemon". A
      // process that loses the bind race exits via the EADDRINUSE handler above
      // without ever having touched daemon.pid, so a failed spawn can no longer
      // convince the app's watchdog that the live daemon died.
      writeOwnDaemonPidFile();
      // TRA-671 counts the start here for the same reason, and not earlier in
      // this action: a process that loses the bind race, or one that exits from
      // the auto-update check above, never becomes the daemon. Counting it as a
      // start would also leave the "running" flag set, so the next real start
      // would report an unclean stop that never happened — and the bind race is
      // exactly the situation this counter exists to measure.
      const wasUncleanStart = recordDaemonStart();
      if (wasUncleanStart) {
        // TRA-2037: the previous run died without running a shutdown handler
        // (SIGKILL, native crash, power loss), so daemon.log holds no reason —
        // only the vitals trajectory before the gap. Log one warn carrying
        // everything knowable post-mortem: counters, launchd's record, and the
        // stall watchdog's death breadcrumb, if it fired. Only the process
        // that won the bind race gets here, so a bind-race loser never logs
        // a diagnosis for a death it did not inherit.
        const counters = readDaemonReliabilityCounters();
        logUncleanStopDiagnosis({
          daemonStarts: counters.starts,
          daemonUncleanStops: counters.uncleanStops,
          launchdExit: getLaunchdLastExit(),
          stallSummary: summarizeStallAlerts(readRecentStallAlerts()),
          // TRA-2056: a V8 heap OOM aborts via SIGABRT with no JS handler —
          // the .ips crash report is the only post-mortem besides vitals.
          oomCrash: readRecentOomCrashes()[0] ?? null,
        });
      }
      // Self-heal: readDaemonPid() unlinks the file whenever it names a dead
      // process, so a single poisoning event used to disarm the guard for the
      // rest of this daemon's life. Re-assert it periodically; the write is
      // skipped unless the file actually differs, so this is idempotent and
      // costs one stat per tick. unref'd — never holds the process open.
      pidReassert = setInterval(reassertOwnDaemonPidFile, PID_REASSERT_INTERVAL_MS);
      pidReassert.unref();
      // TRA-1828: watch for event-loop stalls from bulk indexing. A stall
      // here is the daemon-side counterpart of the clients'
      // `daemon-disappeared` fallback — same incident, observed locally so
      // daemon.log stops being blind to it. Threshold 2 s: the /health
      // client timeout is 500 ms and the watcher needs 30 s of consecutive
      // misses to flip, so a 2 s stall is noteworthy but not yet fatal.
      // TRA-1957: cross-thread stall watchdog. The lag monitor above is
      // same-thread — a daemon parked inside one synchronous span (e.g. a
      // pathological sqlite3_step inside a promise continuation) kills its
      // own timer with /health. The watchdog's worker thread survives that:
      // it appends `stalled` to stall-alerts.jsonl after 10 s without a beat
      // and exits the process after 180 s so launchd respawns (repair scope
      // makes the restart safe). Heartbeat = one lag-monitor tick.
      if (process.env.TRACE_MCP_STALL_WATCHDOG !== '0') {
        const numEnv = (name: string, fallback: number): number => {
          const raw = process.env[name];
          if (raw == null || raw === '') return fallback;
          const n = Number.parseInt(raw, 10);
          return Number.isFinite(n) && n > 0 ? n : fallback;
        };
        try {
          fs.mkdirSync(INDEX_DIR, { recursive: true });
        } catch {
          /* INDEX_DIR is created during daemon init; best-effort here */
        }
        try {
          stallWatchdog = new StallWatchdog({
            alertFile: path.join(INDEX_DIR, STALL_ALERTS_FILENAME),
            checkIntervalMs: 1000,
            alertAfterMs: numEnv('TRACE_MCP_STALL_ALERT_MS', 10_000),
            fatalAfterMs: numEnv('TRACE_MCP_STALL_FATAL_MS', 180_000),
            fatalExit: process.env.TRACE_MCP_STALL_FATAL !== '0',
            onWorkerExit: (info) => {
              logger.error(
                { ...info },
                'Stall watchdog worker died — hard-stall detection offline until restart (TRA-1957)',
              );
            },
          });
          stallWatchdog.start();
        } catch (err) {
          // Monitoring must never break serving: a watchdog that fails to
          // spawn (hardened runtime without worker_threads, exotic platform)
          // degrades to the same-thread lag monitor above.
          logger.warn({ err }, 'Stall watchdog failed to start — continuing without it (TRA-1957)');
          stallWatchdog = null;
        }
      }
      lagMonitor = new EventLoopLagMonitor({
        intervalMs: 1000,
        thresholdMs: 2000,
        onStall: (lagMs, maxLagMs, stallCount) => {
          logger.warn(
            { lagMs, maxLagMs, stallCount, reindexInFlight: countReindexingProjects() },
            'Event loop stall — bulk indexing may be starving /health (TRA-1828)',
          );
        },
        onTick: () => {
          stallWatchdog?.beat();
        },
      });
      lagMonitor.start();
      const projectCount = projectManager.listProjects().length;
      logger.info(
        {
          host,
          port,
          projectCount,
          endpoint: `http://${host}:${port}/mcp`,
          idleExitMinutes: configuredIdleMinutes,
          managedByLaunchd,
        },
        'trace-mcp daemon started',
      );
      // Project model (#199): one daemon serves ALL registered projects. Each
      // MCP session/registration selects its project per connection — via
      // ?project=<abs-path> on /mcp, the X-Trace-Project header, or
      // params._meta["traceMcp/projectRoot"] in initialize. With one registered
      // project that selection is implicit. Cross-repo queries in a SINGLE
      // session are not yet supported (tracked in #199).
      logger.info(
        'Project model: per-session project selection (?project= / X-Trace-Project / _meta). ' +
          'One project per session; not cross-repo within a session.',
      );

      // Phase 5.2: warm tree-sitter init eagerly so the first parse doesn't
      // pay the WASM cold-start tax. Best-effort, fire-and-forget.
      void ensureInitialized().catch(() => {
        /* warm-up failure is non-fatal; lazy path still works */
      });

      // Janitor: reclaim session DBs leaked by SIGKILLed stdio sessions
      // (graceful dispose unlinks them; killed processes don't — observed
      // 60 orphans / 1.9 GB in the field). PID-liveness guarded, never
      // touches a live session. Deferred so it can't delay first requests.
      setImmediate(() => {
        try {
          sweepOrphanedSessionDbs(INDEX_DIR);
        } catch {
          /* janitorial only — never affects daemon startup */
        }
      });

      // Phase 5.1: kick off registered-project loading in the background.
      // HTTP server is already accepting; in-flight requests against a still-
      // indexing project return 503 (Retry-After: 5) so the hook fallback
      // takes over transparently until indexing completes.
      // TRA-1843: backstop for the eternal-`starting` hang — if the bootstrap
      // below has not finished within the budget (a setup path wedged past
      // every per-call bound), name the stuck roots and report ready anyway.
      // Per-project `indexing` status still gates 503s, so `ok` means
      // "serving", never "every index finished". Disarmed on the normal path.
      const disarmStartupWatchdog = armStartupWatchdog({
        getStuckRoots: () =>
          projectManager
            .listProjects()
            .filter((p) => p.status === 'starting' || p.status === 'indexing')
            .map((p) => p.root),
        onStuck: (stuckRoots) => {
          logger.error(
            { stuckRoots },
            'Startup watchdog: registered-project loading did not finish in time — ' +
              'flipping /health to ok so clients stop waiting on "starting" (TRA-1843)',
          );
          startupComplete = true;
        },
      });
      void (async () => {
        try {
          await projectManager.loadAllRegistered();
        } catch (err) {
          logger.error({ err }, 'loadAllRegistered failed during cold-start');
        }

        // #237: startup indexing is done (registered projects loaded, or
        // errored — either way the daemon is warmed and serving, not
        // "starting"). Flip BEFORE the best-effort steps below so /health
        // reports "ok" the moment the blocking work finishes. The cwd-add and
        // grammar warm-up that follow are non-gating.
        startupComplete = true;
        disarmStartupWatchdog();

        // Soft GC (TRA-23): run once now, then hourly for the life of the
        // daemon — long-running daemons otherwise only swept at startup
        // while orphans (e.g. ephemeral per-run workdirs) keep accumulating.
        softGcSweep();
        const softGcTimer = setInterval(softGcSweep, 60 * 60_000);
        softGcTimer.unref();

        // Soft GC (TRA-36): deregistering roots missing >7 days now happens
        // inside softGcSweep() above, which already ran once just now and
        // repeats hourly. It used to be duplicated here with the hourly path
        // calling the grace-less `pruneStaleProjects` instead (TRA-702).

        // Soft GC (TRA-335): that sweep only reaches workdirs that were
        // *deleted*. Agent runtimes that leave their one-shot checkout on disk
        // (Multica, and CI/sandbox workflows shaped like it) leave an entry
        // `prune` will always classify as live, so the registry grew one row —
        // and one permanently-reindexed project — per run, forever. Age since
        // registration is the signal; see ProjectManager.sweepEphemeralProjects.
        //
        // TRA-396: those rows only exist on registries written before one-shot
        // workdirs stopped being persisted at all. Their *DBs* still need
        // collecting, and so do the ones current runs leave behind — neither
        // has a registry row to hang a sweep off, so sweepEphemeralDbs works
        // straight off the index directory and mtime.
        const ephemeralSweep = async () => {
          try {
            const removed = await projectManager.sweepEphemeralProjects();
            if (removed.length > 0) {
              logger.info(
                { removedRoots: removed },
                `Deregistered ${removed.length} stale one-shot workdir project(s)`,
              );
            }
          } catch (err) {
            logger.warn({ err }, 'sweepEphemeralProjects failed (non-fatal)');
          }
          try {
            // TRA-1714: count first so the sweep stays visible in daemon.log
            // even when there is nothing to delete — the hourly silence made
            // a healthy 24h TTL indistinguishable from a sweep that never runs.
            let scanned = 0;
            try {
              scanned = fs.readdirSync(EPHEMERAL_INDEX_DIR).filter((f) => f.endsWith('.db')).length;
            } catch {
              /* dir not created yet — no ephemeral checkout has run here */
            }
            const removedDbs = sweepEphemeralDbs();
            if (removedDbs.length > 0) {
              logger.info(
                { removedDbs },
                `Deleted ${removedDbs.length} abandoned one-shot workdir index DB(s)`,
              );
            } else if (scanned > 0) {
              logger.info(
                { ephemeralDbs: scanned },
                `Ephemeral index sweep: ${scanned} one-shot workdir DB(s) within TTL, nothing to delete`,
              );
            } else {
              logger.debug('Ephemeral index sweep: no one-shot workdir DBs present');
            }
          } catch (err) {
            logger.warn({ err }, 'sweepEphemeralDbs failed (non-fatal)');
          }
          try {
            // TRA-1908: top-level counterpart of the ephemeral sweep above.
            // `orphan_unregistered` DBs (no registry row at all — old naming
            // schemes, razed throwaway checkouts) are only dead past the TTL;
            // a fresh one may just be a project not yet re-added. The sweep
            // itself reports the scanned count so daemon.log stays verifiable
            // either way, mirroring the TRA-1714 ephemeral line above.
            const orphanResult = sweepTopLevelOrphanDbs();
            if (orphanResult.removed.length > 0) {
              logger.info(
                { removedDbs: orphanResult.removed, freedBytes: orphanResult.freedBytes },
                `Deleted ${orphanResult.removed.length} abandoned unregistered index DB(s) (${orphanResult.freedBytes} bytes freed)`,
              );
            } else if (orphanResult.scannedOrphans > 0) {
              logger.info(
                {
                  orphanDbs: orphanResult.scannedOrphans,
                  retainedWithinTtl: orphanResult.retainedWithinTtl,
                },
                `Top-level orphan sweep: ${orphanResult.scannedOrphans} unregistered DB(s) within TTL, nothing to delete`,
              );
            } else {
              logger.debug('Top-level orphan sweep: no unregistered DBs present');
            }
          } catch (err) {
            logger.warn({ err }, 'sweepTopLevelOrphanDbs failed (non-fatal)');
          }
          try {
            // TRA-2062: same story one dir over — status sentinels of razed
            // workdirs were never removed by any deregistration path, so
            // STATUS_DIR grew 3–4 files per agent run with no observer. The
            // per-root deleter (removeProjectArtifacts/sweepMissingRoots)
            // covers new removals; this gated orphan sweep drains the backlog
            // (hash with no registry root and no index DB, past the TTL).
            const statusResult = sweepOrphanStatusSentinels();
            if (statusResult.removed.length > 0) {
              logger.info(
                { removedSentinels: statusResult.removed },
                `Deleted ${statusResult.removed.length} orphaned status sentinel(s)`,
              );
            } else if (statusResult.scannedOrphans > 0) {
              logger.info(
                {
                  orphanSentinels: statusResult.scannedOrphans,
                  retainedWithinTtl: statusResult.retainedWithinTtl,
                },
                `Orphan status sweep: ${statusResult.scannedOrphans} orphaned sentinel(s) within TTL, nothing to delete`,
              );
            } else {
              logger.debug('Orphan status sweep: no orphaned status sentinels present');
            }
          } catch (err) {
            logger.warn({ err }, 'sweepOrphanStatusSentinels failed (non-fatal)');
          }
          // TRA-527: same story one store over — subproject auto-sync wrote a
          // permanent row into the *global* topology DB per run workdir, and no
          // removeProject ever ran for a root that was never registered.
          const droppedTopology = await sweepEphemeralTopology();
          if (droppedTopology.length > 0) {
            logger.info(
              { droppedTopology },
              `Dropped topology rows for ${droppedTopology.length} one-shot workdir(s)`,
            );
          }
        };
        await ephemeralSweep();
        const ephemeralTimer = setInterval(() => void ephemeralSweep(), 60 * 60_000);
        ephemeralTimer.unref();

        // If cwd is a project not yet registered, add it too.
        const cwd = process.cwd();
        if (!projectManager.getProject(cwd)) {
          try {
            const root = findProjectRoot(cwd);
            if (!projectManager.getProject(root)) {
              await projectManager.addProject(root);
            }
          } catch {
            /* cwd is not a project dir — fine */
          }
        }

        // Phase 5.2: now that we have the language set from the registered
        // project DBs, pre-warm the relevant tree-sitter grammars in parallel.
        try {
          const languages = collectKnownLanguages(projectManager.listProjects());
          if (languages.length > 0) {
            void warmUpGrammars(languages).catch(() => {
              /* best-effort warm-up */
            });
          }
        } catch {
          /* never fatal */
        }
      })();
    });
  });

program
  .command('index')
  .description('Index a project directory')
  .argument('[dir]', 'Directory to index (default: current directory)')
  .option('-f, --force', 'Force reindex all files')
  .action(async (dir: string | undefined, opts: { force?: boolean }) => {
    const resolvedDir = path.resolve(dir ?? '.');
    if (!fs.existsSync(resolvedDir)) {
      logger.error({ dir: resolvedDir }, 'Directory does not exist');
      process.exit(1);
    }

    const configResult = await loadConfig(resolvedDir);
    if (configResult.isErr()) {
      logger.error({ error: configResult.error }, 'Failed to load config');
      process.exit(1);
    }
    const config = configResult.value;

    const dbPath = resolveDbPath(resolvedDir);
    ensureGlobalDirs();

    const db = initializeDatabase(dbPath);
    const store = new Store(db);
    const registry = PluginRegistry.createWithDefaults();

    logger.info({ dir: resolvedDir, dbPath, force: opts.force ?? false }, 'Indexing started');

    const pipeline = new IndexingPipeline(store, registry, config, resolvedDir);
    const result = await pipeline.indexAll(opts.force ?? false);
    logger.info(result, 'Indexing completed');
    const skippedDirs = pipeline.getSkippedTopLevelDirs();
    if (skippedDirs.length > 0) {
      logger.info(
        { skippedDirs },
        'Top-level folders skipped by built-in/config ignore rules — see ' +
          '"Getting a skipped folder indexed" in docs/configuration.md',
      );
    }
    updateLastIndexed(resolvedDir);

    // Auto-discover subprojects: register this project, scan contracts & client calls
    await runSubprojectAutoSync(resolvedDir, config);

    await pipeline.dispose();
    db.close();
  });

program
  .command('index-file')
  .description('Incrementally reindex a single file (called by the PostToolUse auto-reindex hook)')
  .argument('<file>', 'Absolute or relative path to the file to reindex')
  .option(
    '--wait',
    'Wait for a running daemon to finish the reindex instead of returning once it is queued (benchmarks)',
  )
  .action(async (file: string, opts: { wait?: boolean }) => {
    const resolvedFile = path.resolve(file);
    if (!fs.existsSync(resolvedFile)) {
      process.exit(0); // file may have been deleted — exit silently
    }

    let projectRoot: string;
    try {
      projectRoot = resolveIndexFileProjectRoot(resolvedFile);
    } catch {
      process.exit(0); // not inside a known project — skip silently
    }

    const outcome = await dispatchIndexFile(resolvedFile, projectRoot, {
      daemonRunning: () => isDaemonRunning(DEFAULT_DAEMON_PORT),
      postToDaemon: () =>
        fetch(
          `http://127.0.0.1:${DEFAULT_DAEMON_PORT}/api/projects/reindex-file${opts.wait ? '?wait=1' : ''}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ project: projectRoot, path: resolvedFile }),
            // A queued request is answered in milliseconds; --wait covers the
            // whole reindex, which takes seconds on a large project.
            signal: AbortSignal.timeout(opts.wait ? 120_000 : 2000),
          },
        ),
      // Same spelling the daemon keys its reindex lock by (TRA-2032 aliases).
      lockRoot: getProject(projectRoot)?.root ?? projectRoot,
      indexLocally: async () => {
        const configResult = await loadConfig(projectRoot);
        if (configResult.isErr()) return;
        const config = configResult.value;

        const dbPath = resolveDbPath(projectRoot);
        ensureGlobalDirs();

        const db = initializeDatabase(dbPath);
        const store = new Store(db);
        const registry = PluginRegistry.createWithDefaults();

        const pipeline = new IndexingPipeline(store, registry, config, projectRoot);
        try {
          await pipeline.indexFiles([indexFilePathForProject(resolvedFile, projectRoot)]);
        } finally {
          await pipeline.dispose();
          db.close();
        }
      },
    });
    const { exitCode, message } = describeIndexFileOutcome(outcome, resolvedFile, projectRoot);
    if (message) process.stderr.write(`${message}\n`);
    // Local indexing ends by itself once the pipeline is disposed.
    if (outcome !== 'local') process.exit(exitCode);
  });

program
  .command('setup-hooks')
  .description(
    'Install Claude Code PreToolUse guard + lifecycle hooks (alias: use `trace-mcp init` instead)',
  )
  .option(
    '--global',
    'Install to ~/.claude/settings.json (default: project-level .claude/settings.local.json)',
  )
  .option('--uninstall', 'Remove the hook(s)')
  .option(
    '--lifecycle',
    'Operate on the lifecycle hooks (SessionStart / UserPromptSubmit / Stop / SessionEnd) instead of the guard hook',
  )
  .option(
    '--strict',
    'Set enforcement tier to strict: hard-deny native Read/Grep/ls/find calls that trace-mcp can serve (default: advisory — warn but allow)',
  )
  .option(
    '--enforce <tier>',
    'Set enforcement tier explicitly: advisory (default), strict, or off',
    'advisory',
  )
  .action(
    (opts: {
      global?: boolean;
      uninstall?: boolean;
      lifecycle?: boolean;
      strict?: boolean;
      enforce?: string;
    }) => {
      if (opts.lifecycle) {
        if (opts.uninstall) {
          const removed = uninstallLifecycleHooks({ global: opts.global });
          console.log(`Removed ${removed.length} lifecycle hook entries.`);
          return;
        }
        const installed = installLifecycleHooks({ global: opts.global });
        for (const r of installed) {
          console.log(`Hook ${r.action}: ${r.target}`);
          if (r.detail) console.log(`  ${r.detail}`);
        }
        return;
      }
      if (opts.uninstall) {
        uninstallGuardHook({ global: opts.global });
        console.log('trace-mcp hook removed.');
        return;
      }
      // --strict shorthand wins over --enforce if both supplied.
      let tier: GuardEnforceTier = 'advisory';
      if (opts.strict) {
        tier = 'strict';
      } else if (opts.enforce) {
        const raw = opts.enforce.toLowerCase();
        if (raw === 'strict' || raw === 'off' || raw === 'advisory') {
          tier = raw as GuardEnforceTier;
        } else {
          console.warn(`Unknown --enforce value "${opts.enforce}" — falling back to advisory.`);
        }
      }
      const result = installGuardHook({ global: opts.global, enforce: tier });
      console.log(`Hook ${result.action}: ${result.target}`);
      if (result.detail) console.log(`  ${result.detail}`);
      if (tier !== 'advisory') {
        console.log(`  Enforcement tier: ${tier} (TRACE_MCP_ENFORCE=${tier})`);
      }
    },
  );

program
  .command('list')
  .description('List all registered projects')
  .option('--json', 'Output as JSON')
  .action((opts: { json?: boolean }) => {
    const projects = listProjects();
    if (opts.json) {
      console.log(JSON.stringify(projects, null, 2));
    } else if (projects.length === 0) {
      console.log('No projects registered. Run `trace-mcp add` in a project directory.');
    } else {
      console.log('Registered projects:\n');
      let staleCount = 0;
      let autoCount = 0;
      for (const p of projects) {
        const lastIdx = p.lastIndexed ? new Date(p.lastIndexed).toLocaleString() : 'never';
        const rootExists = fs.existsSync(p.root);
        const dbExists = fs.existsSync(p.dbPath) ? 'ok' : 'missing';
        // `explicit` is set by `add`/`init`; its absence means the daemon
        // registered this root by itself when a client connected from it
        // (#936). Say so, so the list answers "why is this here?".
        const auto = p.explicit ? '' : '  [auto-added on MCP connect]';
        if (!p.explicit) autoCount++;
        console.log(`  ${p.name}${rootExists ? '' : '  [STALE — folder deleted]'}${auto}`);
        console.log(`    Root: ${p.root}`);
        console.log(`    DB: ${dbExists}`);
        console.log(`    Last indexed: ${lastIdx}`);
        console.log();
        if (!rootExists) staleCount++;
      }
      if (staleCount > 0) {
        console.log(
          `${staleCount} stale entr${staleCount === 1 ? 'y' : 'ies'} (folder deleted). ` +
            'Clean up with `trace-mcp doctor --fix` or `trace-mcp prune --apply`.',
        );
      }
      if (autoCount > 0) {
        console.log(
          `${autoCount} entr${autoCount === 1 ? 'y was' : 'ies were'} added automatically when an ` +
            'MCP client connected from that directory. Drop one with `trace-mcp remove <path>`.',
        );
      }
    }
  });

program.addCommand(initCommand);
program.addCommand(upgradeCommand);
program.addCommand(addCommand);
program.addCommand(removeCommand);
program.addCommand(pruneCommand);
program.addCommand(doctorCommand);
program.addCommand(savingsCommand);
program.addCommand(detectLlmCommand);
program.addCommand(consentCommand);
program.addCommand(ciReportCommand);
program.addCommand(checkCommand);
program.addCommand(scanSecurityCommand);
program.addCommand(clientsCommand);
program.addCommand(bundlesCommand);
program.addCommand(subprojectCommand);
program.addCommand(memoryCommand);
program.addCommand(analyticsCommand);
program.addCommand(benchmarkCommand);
program.addCommand(evalCommand);
program.addCommand(statusCommand);
program.addCommand(visualizeCommand);
program.addCommand(daemonCommand);
program.addCommand(installAppCommand);
program.addCommand(askCommand);
program.addCommand(searchCommand);
program.addCommand(blastCommand);
program.addCommand(exportSecurityContextCommand);

// Tiny synchronous subcommand consumed by the shell PreToolUse guard
// (hooks/trace-mcp-guard.sh). Keeps the guard's secrecy model in lockstep
// with the TypeScript side without making the shell parse TS. Prints a
// single line of JSON to stdout and exits — never spawns the indexer,
// daemon, or any network I/O.
program
  .command('classify-env')
  .description(
    'Classify a .env-shaped file (template / managed / user-secret). Used by the PreToolUse guard.',
  )
  .argument('<file>', 'Absolute or relative path to the file to classify')
  .action(async (file: string) => {
    const { classifyEnvFile } = await import('./utils/env-classifier.js');
    const abs = path.resolve(file);
    let head: string | undefined;
    try {
      const fd = fs.openSync(abs, 'r');
      try {
        const buf = Buffer.alloc(512);
        const bytesRead = fs.readSync(fd, buf, 0, 512, 0);
        head = buf.subarray(0, bytesRead).toString('utf8');
      } finally {
        fs.closeSync(fd);
      }
    } catch {
      // File missing or unreadable — classify by name only. The guard
      // still gets a useful answer (template vs user-secret) so it can
      // decide whether to block or allow.
    }
    const result = classifyEnvFile(abs, head);
    process.stdout.write(`${JSON.stringify(result)}\n`);
  });

// `from: 'node'` explicitly, never commander's auto-detection. When the app
// runs this CLI through its own bundled Electron binary
// (ELECTRON_RUN_AS_NODE=1, see packages/app/src/main/daemon-install.ts),
// `process.versions.electron` is set and commander switches to its Electron
// layout — argv[1] is treated as the first user argument, not the script — so
// `serve-http --port 3741` came out as `error: unknown option '--port'`.
// argv is [runtime, script, ...args] under both node and Electron-as-node.
program.parse(process.argv, { from: 'node' });
